import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle, Mail, Mic, MicOff, ShieldAlert, Trash2, Phone,
  Activity, Loader2, Plus, ArrowLeft, ScanLine, Siren, Swords,
  Building2, Send, Upload, Camera, FileText, Copy, MessageSquareWarning, PhoneCall,
  ShieldCheck, ShieldX, X, ChevronDown, ChevronUp, Shield, Clock, Save,
  PawPrint, Globe2, Lightbulb, HeartPulse, QrCode, Pill
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import VoiceMicButton from "../components/VoiceMicButton";
import ReadAloudFAB, { speakFR, speakGenieFR } from "../components/ReadAloudFAB";
import HelpAndAccessibility from "../components/WelcomeOnboarding";
import CountrySwitcher from "../components/CountrySwitcher";
import LanguageSelector from "../components/LanguageSelector";
import { useT } from "../i18n/LanguageContext";
import { PoliceCapIcon, ScannerIcon, PhoneTriangleIcon, MailTriangleIcon, ShieldIcon, BureauIcon, CarCrashIcon } from "../components/sos-icons";
import AccidentFlow from "../components/AccidentFlow";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { isIOS } from "../lib/platform";

const URGENCY_COLORS = {
  critical: "bg-red-50 text-red-700 border-red-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// ============ "JE NE SUIS PAS SEUL" — Bouton regroupement plainte collective ============
function CollectiveCTA({ alertId, scenario, commune, pays }) {
  const [matchCount, setMatchCount] = useState(null);
  const [open, setOpen] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const ELIGIBLE = ["police", "bagarre", "bureau", "harassment", "animal_abuse", "sans_papiers", "accident", "agression_lgbtq", "scam_text", "scam_voice"];

  useEffect(() => {
    if (!ELIGIBLE.includes(scenario)) return;
    (async () => {
      try {
        const params = new URLSearchParams({ scenario });
        if (commune) params.append("commune", commune);
        if (pays) params.append("pays", pays);
        const r = await api.get(`/collective/matches?${params.toString()}`);
        setMatchCount(r.data?.count ?? 0);
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, commune, pays]);

  if (!ELIGIBLE.includes(scenario)) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Cochez la case de consentement pour continuer."); return; }
    setSubmitting(true);
    try {
      await api.post("/collective/register", {
        alert_id: alertId,
        scenario,
        commune: commune || null,
        pays: pays || null,
        keywords: keywords.trim() || null,
        contact_email: email.trim() || null,
        contact_phone: phone.trim() || null,
        consent_to_be_contacted: consent,
      });
      setSubmitted(true);
      toast.success("Demande enregistrée. Vous serez mis(e) en relation si d'autres victimes correspondent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border-2 border-green-700 rounded-sm p-4 text-sm text-green-900" data-testid="collective-submitted">
        <strong>✅ Demande de regroupement enregistrée.</strong> L'équipe d'Un Avocat en Or vous mettra en contact (anonymement d'abord) si d'autres victimes correspondent à votre situation. Surveillez votre email.
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-700 rounded-sm p-5 space-y-3" data-testid="collective-cta">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-sm bg-purple-700 flex items-center justify-center text-white text-xl flex-shrink-0">🤝</div>
        <div className="flex-1">
          <h3 className="font-serif-display text-lg font-medium text-slate-900 mb-1">Vous n'êtes peut-être pas seul(e)</h3>
          {matchCount !== null && matchCount > 0 ? (
            <p className="text-sm text-purple-900 font-medium">
              <strong>{matchCount}</strong> autre{matchCount > 1 ? "s" : ""} personne{matchCount > 1 ? "s" : ""} {matchCount > 1 ? "ont" : "a"} signalé une situation similaire{commune ? ` dans votre commune (${commune})` : ""}.
              Vous pourriez vous regrouper pour porter plainte collectivement — c'est <strong>beaucoup plus efficace</strong>.
            </p>
          ) : (
            <p className="text-sm text-slate-700">
              Si d'autres victimes vivent la même chose (même agresseur, même rue endommagée, même employeur, même refus administratif…), <strong>se regrouper pour porter plainte collectivement est beaucoup plus efficace</strong>. Une plainte collective coûte moins cher et fait plus de poids.
            </p>
          )}
        </div>
      </div>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full bg-purple-700 hover:bg-purple-800 text-white font-bold py-3 rounded-sm"
          data-testid="collective-open-btn"
        >🤝 Me regrouper avec d'autres victimes</button>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="collective-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Mots-clés (auteur, lieu, employeur...)</label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Ex: rue de la Loi 100 / employeur Lidl Bxl / agent matricule 12345"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
              data-testid="collective-keywords"
            />
            <p className="text-xs text-slate-500 mt-1">Ces mots-clés serviront à matcher d'autres victimes du <strong>même cas</strong>.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Email de contact</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Laisser vide = utilise votre email de compte"
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
                data-testid="collective-email"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Téléphone (optionnel)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+32..."
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
                data-testid="collective-phone"
              />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5"
              data-testid="collective-consent"
            />
            <span>J'accepte d'être contacté(e) par Un Avocat en Or pour être mis(e) en relation (anonymement d'abord) avec d'autres victimes de la même situation, en vue d'une plainte collective.</span>
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || !consent}
              className="flex-1 bg-purple-700 hover:bg-purple-800 disabled:bg-slate-400 text-white font-bold py-3 rounded-sm"
              data-testid="collective-submit-btn"
            >{submitting ? "Envoi..." : "Envoyer ma demande"}</button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-3 text-sm text-slate-600 border border-slate-300 rounded-sm"
              data-testid="collective-cancel-btn"
            >Annuler</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function SOS() {
  const { t } = useT();
  const [alerts, setAlerts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("home"); // home | scan | police | bagarre | bureau | view
  const [working, setWorking] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeInitialTab, setWelcomeInitialTab] = useState("tutorial");

  // Show welcome onboarding on first visit (v2 = added critical permission step)
  useEffect(() => {
    try {
      if (!localStorage.getItem("welcome_shown_v2")) {
        setWelcomeOpen(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Handle option buttons clicks from SOSHome
  useEffect(() => {
    const onTuto = () => { setWelcomeInitialTab("tutorial"); setWelcomeOpen(true); };
    const onAcc = () => { setWelcomeInitialTab("accessibility"); setWelcomeOpen(true); };
    window.addEventListener("open-help-tutorial", onTuto);
    window.addEventListener("open-help-accessibility", onAcc);
    return () => {
      window.removeEventListener("open-help-tutorial", onTuto);
      window.removeEventListener("open-help-accessibility", onAcc);
    };
  }, []);

  const closeWelcome = () => {
    try { localStorage.setItem("welcome_shown_v2", "1"); } catch { /* ignore */ }
    setWelcomeOpen(false);
  };

  // Handle ?action=...&auto=1 from Landing tiles
  useEffect(() => {
    const action = searchParams.get("action");
    if (action && ["scan", "police", "bagarre", "bureau", "scam_text", "scam_voice", "aladin", "bagarre_live", "bagarre_prevention", "accident", "harassment", "animal_abuse", "sans_papiers", "suggestion", "medical", "agression_lgbtq"].includes(action)) {
      setMode(action);
    }
  }, [searchParams]);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get("/sos/alerts"),
        api.get("/chat/conversations"),
      ]);
      setAlerts(a.data);
      setConversations(c.data);
    } catch (e) {
      console.warn("SOS load", e);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar conversations={conversations} onNewChat={() => (window.location.href = "/dashboard")} />
      <main className="flex-1 flex flex-col min-w-0" data-testid="sos-main">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
          <div className="px-4 sm:px-6 py-4 pl-16 md:pl-6 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-red-600">SOS</p>
              <h1 className="font-serif-display text-lg sm:text-xl font-medium tracking-tight text-slate-900 truncate">
                {t("sos_emergency_help") || "Aide d'urgence"}
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageSelector variant="light" compact />
              <CountrySwitcher variant="compact" />
              <Button
                onClick={() => setWelcomeOpen(true)}
                variant="outline"
                className="rounded-sm text-xs sm:text-sm h-9 px-2 sm:px-3"
                data-testid="open-help-button"
                aria-label="Ouvrir le guide d'aide"
              >
                <ShieldAlert className="h-3.5 w-3.5 sm:mr-1.5 text-amber-600" />
                <span className="hidden sm:inline">Aide</span>
              </Button>
              {mode !== "home" && (
                <Button onClick={() => { setMode("home"); setSelected(null); }} variant="ghost" className="rounded-sm h-9 px-2 sm:px-3" data-testid="back-to-sos-home">
                  <ArrowLeft className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Retour</span>
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scroll p-6 pb-32 sm:pb-24">
          <div className="max-w-5xl mx-auto">
            {mode === "home" && (
              <SOSHome
                alerts={alerts}
                onPickAction={(a) => setMode(a)}
                onOpenAlert={(a) => { setSelected(a); setMode("view"); }}
                onReload={load}
              />
            )}
            {mode === "scan" && (
              <ScannerFlow
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {(mode === "police" || mode === "bureau" || mode === "harassment" || mode === "animal_abuse" || mode === "sans_papiers") && (
              <ScenarioFlow
                scenario={mode}
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "medical" && (
              <MedicalHub
                working={working} setWorking={setWorking}
                onScenarioDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "suggestion" && (
              <SuggestionForm
                working={working} setWorking={setWorking}
                onDone={() => setMode("home")}
              />
            )}
            {mode === "bagarre" && (
              <BouclierChoice
                onLive={() => setMode("bagarre_live")}
                onPrevention={() => setMode("bagarre_prevention")}
              />
            )}
            {mode === "bagarre_live" && (
              <ScenarioFlow
                scenario="bagarre"
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "bagarre_prevention" && (
              <BouclierPreventionFlow
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "scam_text" && (
              <ScamMessageFlow
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "scam_voice" && (
              <ScamVoiceFlow
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "aladin" && (
              <AladinLamp
                working={working} setWorking={setWorking}
                onRoute={(action) => setMode(action)}
                onBack={() => setMode("home")}
              />
            )}
            {mode === "agression_lgbtq" && (
              <ScenarioFlow
                scenario="agression_lgbtq"
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "accident" && (
              <AccidentFlow
                working={working} setWorking={setWorking}
                onDone={(a) => { setSelected(a); setMode("view"); load(); }}
              />
            )}
            {mode === "view" && selected && (
              <AlertView alert={selected} onReload={load} onBack={() => { setSelected(null); setMode("home"); }} />
            )}
          </div>
        </div>
      </main>
      <ReadAloudFAB targetSelector='[data-testid="sos-main-content"]' />
      <HelpAndAccessibility open={welcomeOpen} onClose={closeWelcome} initialTab={welcomeInitialTab} />
    </div>
  );
}

function SOSHome({ alerts, onPickAction, onOpenAlert, onReload }) {
  const { t } = useT();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/sos/alerts/${id}`); toast.success("Alerte supprimée"); onReload(); }
    catch { toast.error("Erreur"); }
  };

  const MAIN_TILES = [
    { id: "accident", color_label: "vert", color_key: "color_green", label: t("tile_accident") || "Accident de la route", desc: "Constat IA + appel assureur + police si besoin", icon: CarCrashIcon, bg: "bg-emerald-800", iconColor: "text-white", labelColor: "text-white", testid: "tile-accident" },
    { id: "police", color_label: "bleu marine", color_key: "color_navy", label: t("tile_police") || "Contrôle police", desc: "Loi citée + mail au Comité P si abus", icon: PoliceCapIcon, bg: "bg-blue-900", iconColor: "text-white", labelColor: "text-white", testid: "tile-police" },
    { id: "bagarre", color_label: "orange", color_key: "color_orange", label: t("tile_shield") || "Bouclier", desc: "Conflit en cours OU mode protection préventif", icon: ShieldIcon, bg: "bg-orange-600", iconColor: "text-white", labelColor: "text-white", testid: "tile-bagarre" },
    { id: "medical", color_label: "cyan", color_key: "color_cyan", label: t("tile_medical") || "Médical / Hôpital", desc: "Allergies + traitements + QR code + vérif puff/médicament", icon: HeartPulse, bg: "bg-cyan-800", iconColor: "text-white", labelColor: "text-white", testid: "tile-medical" },
  ];
  const SCAM_TILES = [
    { id: "scam_text", num: 5, color_label: "violet", color_key: "color_purple", label: t("tile_scam_msg") || "Message suspect", desc: "SMS / email / facture — détecte phishing", icon: MailTriangleIcon, bg: "bg-purple-700", iconColor: "text-white", labelColor: "text-white", testid: "tile-scam-text", outline: false },
    { id: "scam_voice", num: 6, color_label: "blanc bordure rouge", color_key: "color_white_red", label: t("tile_scam_call") || "Appel suspect", desc: "Voix clonée, faux proche, deepfake téléphonique", icon: PhoneTriangleIcon, bg: "bg-white", iconColor: "text-red-600", labelColor: "text-slate-900", testid: "tile-scam-voice", outline: true },
  ];
  const ACCIDENT_TILE = { id: "scan", num: 7, color_label: "or", color_key: "color_gold", label: t("tile_scanner") || "Scanner un document", desc: "Photo de courrier → traduction simple + lettre de réponse", icon: ScannerIcon, bg: "bg-[#B89C65]", iconColor: "text-white", labelColor: "text-white", testid: "tile-scan" };
  const HARASSMENT_TILE = { id: "harassment", num: 8, color_label: "rose", color_key: "color_pink", label: t("tile_harassment") || "Harcèlement & Travail", desc: "Travail · École · Apprentissage · Stage — IA + syndicat + courriers prêts", icon: ShieldAlert, bg: "bg-rose-700", iconColor: "text-white", labelColor: "text-white", testid: "tile-harassment" };
  const ANIMAL_TILE = { id: "animal_abuse", num: 9, color_label: "marron", color_key: "color_brown", label: t("tile_animal") || "Maltraitance animale", desc: "Animal battu, négligé, abandonné — IA + plainte police + signalement vétérinaire", icon: PawPrint, bg: "bg-amber-800", iconColor: "text-white", labelColor: "text-white", testid: "tile-animal-abuse" };
  const SANS_PAPIERS_TILE = { id: "sans_papiers", num: 10, color_label: "bleu", color_key: "color_blue", label: t("tile_foreigner") || "Sans-papiers / Étrangers", desc: "Contrôle, démarche, abus — IA répond DANS VOTRE LANGUE et protège vos droits humains", icon: Globe2, bg: "bg-blue-800", iconColor: "text-white", labelColor: "text-white", testid: "tile-sans-papiers" };
  const SUGGESTION_TILE = { id: "suggestion", num: 11, color_label: "jaune", color_key: "color_yellow", label: t("tile_suggestion") || "Suggérer une amélioration", desc: "Vous avez une idée pour améliorer Un Avocat en Or ? Envoyez-la directement au créateur.", icon: Lightbulb, bg: "bg-yellow-700", iconColor: "text-white", labelColor: "text-white", testid: "tile-suggestion" };
  const MEDICAL_TILE = { id: "bureau", num: 12, color_label: "noir", color_key: "color_black", label: t("tile_bureau") || "Bureau officiel", desc: "Notaire, avocat, assureur, admin — abus détectés", icon: BureauIcon, bg: "bg-slate-900", iconColor: "text-[#B89C65]", labelColor: "text-white", testid: "tile-bureau" };

  const ALL_TILES = [...MAIN_TILES.map((t, i) => ({ ...t, num: i + 1, newTile: false })), ...SCAM_TILES.map((t) => ({ ...t, newTile: true }))];

  const TILE_EXPLAINS = [
    { num: 1, title: "Accident de la route", body: "Vous venez d'avoir un accident ? Décrivez-le ou photographiez les véhicules. L'IA pré-remplit votre constat amiable, vous indique quoi noter (témoins, plaques, blessures) et prépare l'appel à votre assureur ainsi qu'à la police si nécessaire." },
    { num: 2, title: "Contrôle police", body: "Un agent vous contrôle ou vous fouille ? Décrivez la situation à l'oral. L'IA cite la loi exacte, vous dit ce que vous DEVEZ faire et ce que vous N'ÊTES PAS obligé(e) de faire, et prépare un mail au Comité P si l'agent abuse." },
    { num: 3, title: "Bouclier", body: "Deux modes en un. 'C'est en cours' : conflit, agression ou dispute physique → caméra + audio + GPS, plainte au procureur prête. 'Préventif' : avant un RDV à risque (huissier, négociation, contrôle) → enregistrement audio que vous gardez 24 h, ou plus si vous le conservez comme preuve." },
    { num: 4, title: "Médical / Hôpital", body: "Votre dossier d'urgence dans votre poche : allergies, traitements, conditions médicales. QR code à montrer aux secouristes, carte médicale PDF imprimable, vérification IA des médicaments et e-cigarettes avant utilisation." },
    { num: 5, title: "Message suspect", body: "Un SMS « votre colis Bpost est bloqué » ? Un email d'un faux huissier ? Une facture étrange ? Collez le texte OU prenez une photo. L'IA détecte les arnaques courantes en Belgique et vous dit en 5 secondes : légitime, suspect ou arnaque certaine." },
    { num: 6, title: "Appel suspect", body: "Votre fils, votre banquier ou votre comptable vous appelle en urgence pour vous demander de l'argent ? Enregistrez l'appel ou uploadez la messagerie vocale. L'IA détecte les patterns d'arnaque téléphonique et vous donne les questions à poser pour démasquer un imposteur." },
  ];

  const SITUATIONS = [
    { emoji: "🚗", title: "« Je viens d'avoir un accident de voiture, qu'est-ce que je fais ? »", tile: "Touche 1 — Accident" },
    { emoji: "👮", title: "« Un policier m'arrête et veut fouiller mon sac »", tile: "Touche 2 — Police" },
    { emoji: "🏛️", title: "« Je vais voir mon notaire et il a tendance à m'arnaquer »", tile: "Touche 3 — Bouclier préventif" },
    { emoji: "🏥", title: "« Je dois donner mes infos médicales aux secouristes »", tile: "Touche 4 — Médical" },
    { emoji: "📱", title: "« J'ai un SMS bizarre de Bpost qui demande 2,99 € »", tile: "Touche 5 — Message" },
    { emoji: "📞", title: "« Mon \"fils\" m'appelle en pleurs pour 800 € urgent »", tile: "Touche 6 — Appel" },
  ];

  const FAQ = [
    { q: "❤️ Ça veut dire quoi le compteur « X personnes aidées » sur la page d'accueil ?", a: "Il compte les VRAIS utilisateurs (pas les comptes de test) qui ont utilisé au moins une touche SOS. Il monte tout seul. Vous pouvez le partager en un clic avec vos proches pour faire connaître le service." },
    { q: "👥 C'est quoi le badge « Vous n'êtes pas seul·e » sur les tuiles ?", a: "Il montre combien de personnes ont vécu la MÊME situation que vous (contrôle policier abusif, harcèlement, maltraitance…). Vous pouvez rejoindre une plainte collective : votre témoignage, ajouté aux autres, fait pression sur les autorités et augmente vos chances de justice." },
    { q: "🩺 La Consultation Sécurisée, comment ça marche ?", a: "Dans la tuile Médical (Touche 1), un 4ᵉ onglet « Consultation sécurisée » utilise le micro AVEC l'accord de votre médecin. Il écoute la prescription en direct. Si le médecin prononce un composant auquel vous êtes allergique (pénicilline, aspirine…), votre téléphone VIBRE FORT et affiche une alerte rouge : signalez-le au médecin avant qu'il signe. Ça peut sauver une vie." },
    { q: "Est-ce que c'est légal d'enregistrer un appel ou un rendez-vous ?", a: "Oui, si vous y participez. En Belgique (article 314bis du Code pénal), vous pouvez enregistrer une conversation dans laquelle vous êtes vous-même partie prenante, pour votre usage privé. Vous ne pouvez pas la partager publiquement ou l'utiliser comme preuve sans le consentement des autres personnes." },
    { q: "Est-ce que je peux utiliser ces enregistrements au tribunal ?", a: "Dans la plupart des cas, oui — la jurisprudence belge admet les enregistrements faits par une partie à la conversation, à condition qu'ils n'aient pas été obtenus de manière déloyale (provocation, piégeage). Consultez toujours un avocat avant de produire un enregistrement en justice." },
    { q: "Est-ce que mes données sont privées ?", a: "Oui. Vos enregistrements et conversations sont chiffrés et stockés en Europe (RGPD). Le mode Bouclier préventif s'efface automatiquement après 24 h sauf si vous le conservez. Nous ne vendons aucune donnée." },
    { q: "Est-ce que c'est gratuit ?", a: isIOS()
        ? "Oui, dans la version iPhone toutes les fonctions essentielles sont gratuites. Il n'y a pas d'achat depuis l'application. Si vous souhaitez un accompagnement pour votre organisation (assurance, mutuelle, syndicat, entreprise), écrivez-nous à pro@unavocatenor.com."
        : "Les fonctions essentielles sont gratuites pour usage personnel modéré. Trois formules payantes : Individuel 9,50 €/mois, Famille 14,50 €/mois (illimité enfants), et packs Entreprises (TPE 249 € · PME 399 € · Corporate 449 € · Grand Compte 2,49 €/employé)." },
    { q: "L'IA peut-elle remplacer un avocat ?", a: "Non, et ce n'est pas son but. Un Avocat en Or vous outille en 5 secondes pour réagir bien dans 80 % des cas du quotidien. Pour un dossier complexe, une procédure judiciaire ou un montage juridique, consultez un avocat — l'app peut même vous aider à préparer le rendez-vous." },
    { q: "Et si l'IA se trompe ?", a: "Toutes les réponses citent la source (article de loi, organisme). Vérifiez toujours avant d'agir, surtout dans des cas inhabituels. L'app est un assistant, pas un oracle. Si vous avez un doute, posez une question complémentaire ou contactez un professionnel." },
  ];

  return (
    <div className="space-y-10" data-testid="sos-main-content">
      {/* SECTION 1 — TUILES */}
      <section data-testid="section-tiles">
        <p className="text-xs uppercase tracking-[0.2em] text-red-600 mb-2">Urgence · Accès rapide</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">Que se passe-t-il ?</h2>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {MAIN_TILES.map((tile, i) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                onClick={() => onPickAction(tile.id)}
                className={`group ${tile.bg} ${tile.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] transition-all duration-200 min-h-[170px] sm:min-h-[200px] flex flex-col justify-between`}
                data-testid={tile.testid}
              >
                <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-sm bg-white/15 flex items-center justify-center ${tile.iconColor}`}>
                  <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <p className={`text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1`}>Touche {i + 1} · {t(tile.color_key) || tile.color_label}</p>
                  <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{tile.label}</h3>
                  <p className="text-xs opacity-80 leading-relaxed line-clamp-2 hidden sm:block">{tile.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-2 mt-5 mb-2" data-testid="scam-scroll-hint">
          <ChevronDown className="h-4 w-4 text-amber-600 animate-bounce" />
          <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-medium">{t("sos_anti_scam") || "Anti-arnaque numérique"}</p>
          <ChevronDown className="h-4 w-4 text-amber-600 animate-bounce" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {SCAM_TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                onClick={() => onPickAction(tile.id)}
                className={`group ${tile.bg} ${tile.labelColor} rounded-sm p-4 sm:p-5 text-left hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] transition-all duration-200 min-h-[140px] flex flex-col justify-between ${tile.outline ? "border-2 border-red-600" : ""}`}
                data-testid={tile.testid}
              >
                <div className={`h-11 w-11 sm:h-12 sm:w-12 rounded-sm ${tile.outline ? "bg-red-50" : "bg-white/15"} flex items-center justify-center ${tile.iconColor}`}>
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div>
                  <p className={`text-[10px] uppercase tracking-[0.2em] opacity-80 mb-1`}>Touche {tile.num} · {t(tile.color_key) || tile.color_label}</p>
                  <h3 className="font-serif-display text-base sm:text-lg font-medium leading-tight mb-1">{tile.label}</h3>
                  <p className="text-[10px] sm:text-xs opacity-70 leading-relaxed line-clamp-2">{tile.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Tuile Accident — pleine largeur, mise en avant */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(ACCIDENT_TILE.id)}
            className={`group w-full ${ACCIDENT_TILE.bg} ${ACCIDENT_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[110px]`}
            data-testid={ACCIDENT_TILE.testid}
          >
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${ACCIDENT_TILE.iconColor} flex-shrink-0`}>
              <ACCIDENT_TILE.icon className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 7 · {t("color_gold") || "or"}</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{ACCIDENT_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{ACCIDENT_TILE.desc}</p>
            </div>
          </button>
        </div>

        {/* Tuile Harcèlement & Travail — pleine largeur, public jeunes + adultes */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(HARASSMENT_TILE.id)}
            className={`group w-full ${HARASSMENT_TILE.bg} ${HARASSMENT_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[110px]`}
            data-testid={HARASSMENT_TILE.testid}
          >
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${HARASSMENT_TILE.iconColor} flex-shrink-0`}>
              <HARASSMENT_TILE.icon className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 8 · {t("color_pink") || "rose"}</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{HARASSMENT_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{HARASSMENT_TILE.desc}</p>
            </div>
          </button>
        </div>

        {/* Tuile 9 — Maltraitance animale */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(ANIMAL_TILE.id)}
            className={`group w-full ${ANIMAL_TILE.bg} ${ANIMAL_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[110px]`}
            data-testid={ANIMAL_TILE.testid}
          >
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${ANIMAL_TILE.iconColor} flex-shrink-0`}>
              <ANIMAL_TILE.icon className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 9 · {t("color_brown") || "marron"}</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{ANIMAL_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{ANIMAL_TILE.desc}</p>
            </div>
          </button>
        </div>

        {/* Tuile 10 — Sans-papiers / Étrangers */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(SANS_PAPIERS_TILE.id)}
            className={`group w-full ${SANS_PAPIERS_TILE.bg} ${SANS_PAPIERS_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[110px]`}
            data-testid={SANS_PAPIERS_TILE.testid}
          >
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${SANS_PAPIERS_TILE.iconColor} flex-shrink-0`}>
              <SANS_PAPIERS_TILE.icon className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 10 · {t("color_blue") || "bleu"} 🌍</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{SANS_PAPIERS_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{SANS_PAPIERS_TILE.desc}</p>
            </div>
          </button>
        </div>

        {/* Tuile 12 — Médical / Hôpital */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(MEDICAL_TILE.id)}
            className={`group w-full ${MEDICAL_TILE.bg} ${MEDICAL_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[110px]`}
            data-testid={MEDICAL_TILE.testid}
          >
            <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${MEDICAL_TILE.iconColor} flex-shrink-0`}>
              <MEDICAL_TILE.icon className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 12 · {t("color_black") || "noir"} 💊</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{MEDICAL_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{MEDICAL_TILE.desc}</p>
            </div>
          </button>
        </div>

        {/* Tuile 13 — SOS Agression / Viol / LGBTQI+ — bouton panique rouge */}
        <div className="mt-4 sm:mt-6">
          <button
            onClick={() => onPickAction("agression_lgbtq")}
            className="group w-full bg-red-700 hover:bg-red-800 text-white rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-2xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[140px] border-4 border-red-900 animate-pulse-subtle"
            data-testid="tile-agression-lgbtq"
            aria-label="Touche 13 : Bouton panique — Agression, viol, violence LGBTQI+"
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/20 flex items-center justify-center text-white flex-shrink-0 ring-4 ring-white/30">
              <ShieldAlert className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-90 mb-1 font-bold">Touche 13 · {t("color_red") || "rouge"} · 🚨 URGENCE VITALE 🚨</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold leading-tight mb-1">SOS Agression · Viol · LGBTQI+</h3>
              <p className="text-xs opacity-90 leading-relaxed">Vous êtes en danger ? Touchez ici → caméra + audio + GPS s'activent et constituent la preuve. Numéros d'aide spécialisés affichés immédiatement.</p>
            </div>
          </button>
        </div>

        {/* Tuile 11 — Suggestions d'amélioration */}
        <div className="mt-3 sm:mt-4">
          <button
            onClick={() => onPickAction(SUGGESTION_TILE.id)}
            className={`group w-full ${SUGGESTION_TILE.bg} ${SUGGESTION_TILE.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[100px]`}
            data-testid={SUGGESTION_TILE.testid}
          >
            <div className={`h-14 w-14 sm:h-16 sm:w-16 rounded-sm bg-white/15 flex items-center justify-center ${SUGGESTION_TILE.iconColor} flex-shrink-0`}>
              <SUGGESTION_TILE.icon className="h-8 w-8 sm:h-10 sm:w-10" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 11 · {t("color_yellow") || "jaune"} 💬</p>
              <h3 className="font-serif-display text-base sm:text-lg font-medium leading-tight mb-1">{SUGGESTION_TILE.label}</h3>
              <p className="text-xs opacity-80 leading-relaxed">{SUGGESTION_TILE.desc}</p>
            </div>
          </button>
        </div>
      </section>

      {/* SECTION 1.5 — LAMPE D'ALADIN — bloc standalone entre les tuiles et les explications */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 border-y border-amber-200 -mx-4 sm:mx-0 sm:rounded-sm px-4 sm:px-8 py-8 sm:py-10" data-testid="section-aladin">
        <div className="max-w-md mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-700 mb-2">Bouton magique · Bêta</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900 mb-3">
            Vous ne savez pas quelle touche utiliser ?
          </h2>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed">
            Frottez la lampe et expliquez votre situation à voix haute.
            L'IA choisit pour vous la bonne touche, et la lampe vous répond en français.
          </p>
          <button
            onClick={() => onPickAction("aladin")}
            className="group relative bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white rounded-full h-24 w-24 sm:h-28 sm:w-28 hover:scale-110 active:scale-95 transition-all duration-200 shadow-2xl hover:shadow-amber-500/60 flex items-center justify-center mx-auto"
            data-testid="tile-aladin"
            aria-label="Frottez la lampe — triage vocal"
          >
            <span className="text-5xl sm:text-6xl group-hover:animate-pulse">🪔</span>
            <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping" />
          </button>
          <p className="text-sm font-medium text-slate-900 mt-4">Touchez la lampe pour parler</p>
          <p className="text-xs text-slate-500 mt-1">La lampe vous répondra avec la voix du génie</p>
        </div>
      </section>

      {/* SECTION 2 — EXPLICATION DES TUILES */}
      <section className="border-t border-slate-200 pt-10" data-testid="section-explainers">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">À quoi sert chaque touche</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">Comment utiliser l'application</h2>
        <div className="space-y-3">
          {TILE_EXPLAINS.map((e) => (
            <div key={e.num} className="bg-white border border-slate-200 rounded-sm p-4 sm:p-5 flex gap-4 items-start" data-testid={`explain-${e.num}`}>
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-sm bg-slate-900 text-[#D4AF37] flex items-center justify-center font-sans text-3xl sm:text-4xl font-bold flex-shrink-0 ring-2 ring-[#D4AF37]/30">
                {e.num}
              </div>
              <div className="min-w-0">
                <h3 className="font-serif-display text-base sm:text-lg font-medium text-slate-900 mb-1">{e.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{e.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 3 — COMPTEUR */}
      {stats && (stats.scams_blocked_7d + stats.documents_decoded_30d + stats.alerts_total > 0) && (
        <section className="border-t border-slate-200 pt-10" data-testid="section-stats">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2 text-center">L'app en action</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6 text-center">Ce que nous avons fait cette semaine</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-scams">
              <p className="font-serif-display text-3xl sm:text-4xl font-medium text-slate-900">{stats.scams_blocked_7d}</p>
              <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">arnaques détectées<br/>cette semaine</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-docs">
              <p className="font-serif-display text-3xl sm:text-4xl font-medium text-slate-900">{stats.documents_decoded_30d}</p>
              <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">documents décodés<br/>(30 j)</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-total">
              <p className="font-serif-display text-3xl sm:text-4xl font-medium text-slate-900">{stats.alerts_total}</p>
              <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">personnes<br/>accompagnées</p>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 4 — OPTIONS */}
      <section className="border-t border-slate-200 pt-10" data-testid="section-options">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Options & paramètres</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">Personnaliser l'application</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => window.dispatchEvent(new Event("open-help-tutorial"))} className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-slate-900 transition-colors" data-testid="option-tutorial">
            <p className="text-2xl mb-2">📖</p>
            <p className="font-medium text-slate-900 text-sm mb-1">Guide d'utilisation</p>
            <p className="text-xs text-slate-500">4 étapes pour découvrir l'app</p>
          </button>
          <button onClick={() => window.dispatchEvent(new Event("open-help-accessibility"))} className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-slate-900 transition-colors" data-testid="option-accessibility">
            <p className="text-2xl mb-2">🔊</p>
            <p className="font-medium text-slate-900 text-sm mb-1">Accessibilité</p>
            <p className="text-xs text-slate-500">Lecture vocale, malvoyants, seniors</p>
          </button>
          <a href="/pro" target="_blank" rel="noopener noreferrer" className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-slate-900 transition-colors" data-testid="option-pro">
            <p className="text-2xl mb-2">🏢</p>
            <p className="font-medium text-slate-900 text-sm mb-1">Pour les pros</p>
            <p className="text-xs text-slate-500">Assurances, mutuelles, CPAS</p>
          </a>
        </div>
      </section>

      {/* SECTION 5 — EXEMPLES DE SITUATIONS */}
      <section className="border-t border-slate-200 pt-10" data-testid="section-situations">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Cas concrets</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">Exemples de situations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SITUATIONS.map((s, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-4 flex items-start gap-3" data-testid={`situation-${i}`}>
              <span className="text-2xl flex-shrink-0">{s.emoji}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 leading-snug mb-1">{s.title}</p>
                <p className="text-xs text-[#B89C65] uppercase tracking-widest">→ {s.tile}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 6 — FAQ */}
      <section className="border-t border-slate-200 pt-10" data-testid="section-faq">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Questions fréquentes</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">Avant d'utiliser, vous voulez savoir…</h2>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <details key={i} className="bg-white border border-slate-200 rounded-sm group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer p-4 flex items-start justify-between gap-3 list-none">
                <p className="text-sm font-medium text-slate-900 leading-snug">{item.q}</p>
                <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform group-open:rotate-180" />
              </summary>
              <p className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* SECTION 7 — HISTORIQUE */}
      {alerts.length > 0 && (
        <section className="border-t border-slate-200 pt-10" data-testid="section-history">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Vos alertes récentes</p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/sos/alerts-export.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-700 underline hover:text-slate-900 flex items-center gap-1"
              data-testid="export-alerts-pdf"
            >
              📥 Exporter tout en PDF
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alerts.map((a) => (
              <button
                key={a.alert_id}
                onClick={() => onOpenAlert(a)}
                className="group bg-white border border-slate-200 rounded-sm p-5 text-left hover:border-slate-900 hover:shadow-md transition-all"
                data-testid={`alert-card-${a.alert_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs uppercase tracking-widest text-[#B89C65]">{a.scenario || a.trigger}</span>
                  <span
                    onClick={(e) => handleDelete(a.alert_id, e)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-900 line-clamp-2 mb-2">
                  {a.label || a.subject || a.parsed?.situation || a.parsed?.type_document || "Alerte"}
                </p>
                <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString("fr-BE")}</p>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============ SCANNER ============
function ScannerFlow({ working, setWorking, onDone }) {
  const [file, setFile] = useState(null);
  const [context, setContext] = useState("");
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-launch camera if ?auto=1 (clicked from Landing)
  useEffect(() => {
    if (searchParams.get("auto") === "1" && fileRef.current) {
      // Slight delay to ensure mount is complete on mobile
      const t = setTimeout(() => {
        try { fileRef.current?.click(); } catch {}
        // Clear param so it doesn't re-fire
        searchParams.delete("auto");
        setSearchParams(searchParams, { replace: true });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [searchParams, setSearchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sélectionnez une photo du document"); return; }
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (context) fd.append("context", context);
      const res = await api.post("/sos/scan-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("Document analysé");
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setWorking(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-8" data-testid="scanner-form">
      <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Touche 1 · Scanner</p>
      <h2 className="font-serif-display text-3xl font-medium mb-2">Scanner un document officiel</h2>
      <p className="text-sm text-slate-500 mb-8">
        Courrier d'administration, contravention, jugement, assignation, avis fiscal…
        Prenez une photo, l'IA le traduit en français simple et prépare la lettre de réponse.
      </p>

      <div
        className="border border-dashed border-slate-300 rounded-sm p-10 text-center cursor-pointer hover:border-slate-900 hover:bg-slate-50 transition-all mb-6"
        onClick={() => fileRef.current?.click()}
        data-testid="scan-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="scan-file-input"
        />
        {file ? (
          <div>
            <ScanLine className="h-10 w-10 mx-auto text-[#B89C65] mb-3" />
            <p className="text-sm font-medium text-slate-900">{file.name}</p>
            <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · cliquer pour changer</p>
          </div>
        ) : (
          <div>
            <Camera className="h-10 w-10 mx-auto text-slate-400 mb-3" />
            <p className="text-base font-medium text-slate-700">Prendre ou choisir une photo</p>
            <p className="text-xs text-slate-500 mt-1">JPEG/PNG/WEBP · max 15MB</p>
          </div>
        )}
      </div>

      <div className="mb-6">
        <Label className="text-xs uppercase tracking-widest mb-2 block">Contexte (optionnel)</Label>
        <div className="relative">
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={2}
            placeholder="Ex: J'ai déjà contesté une fois, je suis locataire depuis 2 ans..."
            className="rounded-sm pr-12"
            data-testid="scan-context-input"
          />
          <div className="absolute top-2 right-2">
            <VoiceMicButton currentValue={context} onTranscript={setContext} testid="scan-context-voice" />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={working}
        className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8 w-full sm:w-auto"
        data-testid="scan-submit-button"
      >
        {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse en cours...</> : <><ScanLine className="h-4 w-4 mr-2" />Analyser et préparer la réponse</>}
      </Button>
    </form>
  );
}

// ============ SCÉNARIOS ============
const SCENARIO_META = {
  police: { label: "Contrôle de police", eyebrow: "Touche 2 · Police", desc: "Décrivez ou enregistrez ce que dit l'agent. L'IA vous rappelle la loi et prépare un mail au Comité P.", icon: Siren, color: "bg-red-600" },
  bagarre: { label: "Bagarre / Conflit public", eyebrow: "Touche 3 · Bagarre", desc: "Décrivez ou enregistrez la situation. L'IA vous aide à désescalader et prépare une plainte si besoin.", icon: Swords, color: "bg-orange-600" },
  bureau: { label: "Bureau officiel", eyebrow: "Touche 4 · Bureau", desc: "Notaire, avocat, assureur, admin. L'IA détecte les abus et prépare un courrier au supérieur/ordre.", icon: Building2, color: "bg-slate-900" },
  harassment: { label: "Harcèlement & Travail", eyebrow: "Touche 8 · Travail", desc: "Harcèlement au travail, à l'école, en apprentissage. L'IA vous oriente vers le syndicat, l'inspection, et prépare les courriers.", icon: ShieldAlert, color: "bg-rose-700" },
  animal_abuse: { label: "Maltraitance animale", eyebrow: "Touche 9 · Animaux", desc: "Animal battu, négligé, abandonné. L'IA cite la loi, prépare la plainte police + signalement vétérinaire officiel.", icon: PawPrint, color: "bg-amber-800" },
  sans_papiers: { label: "Sans-papiers / Étrangers", eyebrow: "Touche 10 · Étrangers", desc: "Contrôle, démarche admin, abus de fonctionnaire. L'IA répond DANS VOTRE LANGUE et protège vos droits humains fondamentaux.", icon: Globe2, color: "bg-blue-800" },
  medical: { label: "Vérification médicale", eyebrow: "Touche 12 · Santé", desc: "Médicament douteux, interaction, puff/psychotrope. L'IA croise votre dossier médical et alerte les risques.", icon: HeartPulse, color: "bg-cyan-800" },
  agression_lgbtq: { label: "SOS Agression / Viol / LGBTQI+", eyebrow: "Touche 13 · Urgence vitale", desc: "Vous vous sentez en danger MAINTENANT. La caméra, l'audio et le GPS s'activent automatiquement pour constituer une preuve. L'IA prépare une plainte spécialisée et liste les aides immédiates.", icon: ShieldAlert, color: "bg-red-700" },
};

// Pre-loaded templates — 1 tap fills the transcript so stressed users save 10s
const SCENARIO_TEMPLATES = {
  police: [
    { label: "Contrôle d'identité sans motif", text: "Un policier me demande mes papiers en pleine rue sans m'expliquer pourquoi, et refuse de me dire pourquoi je suis contrôlé." },
    { label: "Fouille sans motif", text: "Un policier veut fouiller mon sac/véhicule sans me donner de motif et sans présenter sa carte de service." },
    { label: "Garde à vue", text: "Je viens d'être placé en garde à vue. On ne m'a pas dit clairement de quoi on m'accuse ni mes droits." },
    { label: "Verbalisation contestée", text: "L'agent me verbalise pour une infraction que je n'ai pas commise et refuse de m'écouter." },
    { label: "Insultes / brutalité", text: "L'agent m'a insulté / a usé d'une force que j'estime disproportionnée pendant le contrôle." },
  ],
  bagarre: [
    { label: "Conflit de voisinage", text: "Mon voisin m'agresse verbalement, fait du bruit excessif depuis des semaines, et a menacé de me frapper." },
    { label: "Altercation en rue", text: "Une personne inconnue m'insulte et me bouscule en pleine rue devant témoins." },
    { label: "Conflit familial", text: "Un membre de ma famille m'a frappé/menacé pendant une dispute. Il y avait des témoins." },
    { label: "Harcèlement", text: "Je suis harcelé(e) régulièrement (messages, contacts non désirés, suivi). Comment me protéger ?" },
    { label: "Vol / agression", text: "Quelqu'un vient de me voler / me frapper. Je suis en sécurité maintenant mais je veux porter plainte." },
  ],
  bureau: [
    { label: "Notaire frais cachés", text: "Le notaire veut me faire signer un acte avec des frais non prévus dans le devis initial. Il me presse de signer rapidement." },
    { label: "Avocat sans mandat", text: "Mon avocat agit sans m'avoir consulté et me facture des prestations dont je n'ai pas été informé." },
    { label: "Assureur refus indemnisation", text: "Mon assureur refuse de m'indemniser sans m'expliquer la base légale. Je conteste sa décision." },
    { label: "Banque clause abusive", text: "Ma banque applique des frais ou une clause que je trouve abusive et refuse de la justifier." },
    { label: "Admin refus / délai", text: "L'administration refuse ma demande / dépasse les délais légaux sans motivation. Je veux saisir le médiateur." },
    { label: "Mutuelle remboursement", text: "Ma mutuelle refuse de rembourser un soin couvert par mon contrat sans motivation valable." },
  ],
  harassment: [
    { label: "📱 Cyber-harcèlement (réseaux sociaux)", text: "Je subis du cyber-harcèlement sur [Instagram / TikTok / Snapchat / WhatsApp / autre]. Indices sur le(s) harceleur(s) : pseudo [...], école/employeur [...], identité présumée [...]. Je veux : (1) mise en demeure formelle au harceleur s'il est identifié, (2) signalement à la plateforme, (3) plainte au procureur avec demande de réquisition d'identification, (4) si scolaire : signalement au directeur d'école." },
    { label: "🏫 Harcèlement scolaire (école / fac)", text: "Je subis du harcèlement à l'école / fac / centre de formation : moqueries, mise à l'écart, violences. Auteur(s) : [élève(s) / enseignant(s)]. Lieu : [...]. Je veux une mise en demeure au harceleur ET un courrier au directeur de l'établissement." },
    { label: "Harcèlement moral au bureau", text: "Mon chef me dénigre devant les collègues, me confie des tâches impossibles, me crie dessus régulièrement depuis des semaines. Je n'arrive plus à dormir." },
    { label: "Harcèlement sexuel", text: "Un collègue / supérieur me fait des avances déplacées, commentaires sur mon physique, gestes inappropriés. Cela continue malgré mes refus." },
    { label: "Apprenti maltraité", text: "Je suis apprenti(e), mon patron me fait travailler 50h+/semaine, ne me forme pas, m'humilie et menace de me virer si je me plains." },
    { label: "Stage non payé / illégal", text: "Je fais un stage de plus de 2 mois mais je ne reçois aucune gratification, je remplace un poste plein, et je n'ai pas de convention valable." },
    { label: "Salaire non payé / heures sup", text: "Mon employeur ne paie pas mon salaire à temps, refuse de payer mes heures supplémentaires effectuées, me menace si je conteste." },
    { label: "Discrimination embauche / promo", text: "On me refuse une promotion / un poste à cause de mon genre, mon âge, mon origine, ma religion ou mon handicap." },
    { label: "Bizutage", text: "Je subis des intimidations / actes humiliants dans le cadre d'un bizutage / rite d'intégration. C'est interdit par la loi." },
    { label: "Mobbing collectif", text: "Mes collègues s'organisent pour m'isoler, ne pas me parler, me ridiculiser. La direction ferme les yeux." },
  ],
  animal_abuse: [
    { label: "Chien battu par voisin", text: "Mon voisin frappe son chien régulièrement, je l'entends crier. L'animal est attaché en permanence sans eau ni abri." },
    { label: "Chat abandonné", text: "J'ai trouvé un chat manifestement abandonné, en très mauvais état, sans collier, dans une cage/à la rue depuis plusieurs jours." },
    { label: "Cheval/bovin négligé", text: "Je vois un cheval / une vache dans un pré sans eau, sans abri, manifestement amaigri, sans soins depuis longtemps." },
    { label: "Élevage / chenil clandestin", text: "Je suspecte un élevage illégal de chiens ou un commerce d'animaux dans des conditions inacceptables (cages sales, animaux malades)." },
    { label: "Combat de chiens / coqs", text: "Je suis témoin / j'ai des indices d'organisation de combats d'animaux dans un lieu précis." },
    { label: "Transport inhumain", text: "J'ai vu un transport d'animaux (camion, voiture) où ils sont entassés, sans eau, ventilation, en souffrance manifeste." },
  ],
  sans_papiers: [
    { label: "🗺️ Aidez-moi à obtenir mes papiers", text: "Je veux obtenir / renouveler / régulariser mes papiers en [BELGIQUE / FRANCE / LUXEMBOURG]. Ma situation : [explique ta situation - asile / regroupement / titre de séjour / récépissé / autre]. Je suis actuellement à [VILLE]. Aide-moi : quel bureau aller voir, comment m'y rendre, quels documents apporter, comment préparer le rendez-vous. ACTIVE LE GPS pour avoir un itinéraire précis." },    { label: "🛂 Demande d'asile en cours", text: "Je suis demandeur d'asile, ma procédure est en cours. Je veux savoir quel est le prochain bureau à aller voir et comment faire avancer mon dossier. Je suis à [VILLE]." },
    { label: "Contrôle police étranger", text: "Je suis étranger(e), la police m'a arrêté(e) et me demande mes papiers. Je n'ai pas tout sur moi / je n'ai pas de titre de séjour valide. Je ne sais pas ce que je peux dire ou refuser." },
    { label: "Refoulement / rétention", text: "On veut me placer en centre fermé / m'expulser / me refouler à la frontière. Je crains pour ma vie dans mon pays d'origine." },
    { label: "Refus admin sans motif", text: "L'administration (Office des Étrangers / préfecture / OFII) refuse ma demande de séjour / asile / regroupement sans explication écrite ni recours possible." },
    { label: "Pas d'interprète", text: "On me fait signer des documents en français sans me les traduire. Je ne comprends pas ce que je signe." },
    { label: "Refusé à l'hôpital", text: "Un hôpital ou médecin refuse de me soigner parce que je n'ai pas de papiers, alors que c'est urgent." },
    { label: "Demandeur d'asile en attente", text: "Je suis demandeur d'asile, je n'ai pas de réponse depuis des mois, je n'ai ni hébergement ni accès au travail. Que faire ?" },
  ],
  medical: [
    { label: "Vérifier un médicament", text: "Le médecin / pharmacien me prescrit [NOM DU MÉDICAMENT]. Je veux vérifier qu'il est compatible avec mon dossier (allergies, traitements en cours)." },
    { label: "Puff / Vape achetée", text: "J'ai acheté une puff / vape de la marque [MARQUE], goût [GOÛT]. Composition affichée : nicotine X mg, autres ingrédients [...]. Est-ce dangereux pour moi vu mon dossier ?" },
    { label: "Psychotrope légal vendu en boutique", text: "J'ai trouvé en boutique un produit [NOM] vendu comme psychotrope légal (CBD, kratom, champignon, salvia, etc.). Risque pour moi ?" },
    { label: "Hôpital — traitement contesté", text: "À l'hôpital, on veut me donner [TRAITEMENT] mais je suis allergique à [X] / je prends déjà [Y]. Comment refuser sans risque ?" },
    { label: "Mélange médicament + alcool", text: "Je prends [MÉDICAMENT]. Puis-je boire un verre d'alcool / consommer du cannabis / autre ?" },
    { label: "Surdosage suspect", text: "Je / un proche a pris trop de [MÉDICAMENT/PRODUIT]. Symptômes : [...]. Que faire ?" },
  ],
  agression_lgbtq: [
    { label: "🚨 JE SUIS EN DANGER MAINTENANT", text: "Je me sens en danger maintenant. Activez l'enregistrement et le GPS. Décrivez vocalement ou par écrit ce qui se passe au fur et à mesure." },
    { label: "🏳️‍🌈 Agression homophobe / transphobe", text: "Je viens d'être / suis en train d'être agressé(e) ou insulté(e) parce que je suis [gay / lesbienne / trans / bi / queer / intersexe]. Lieu : [DÉCRIRE]. Personnes impliquées : [DÉCRIRE]. Je veux porter plainte ET avoir une protection." },
    { label: "💔 Viol / agression sexuelle", text: "Je viens d'être victime d'une agression sexuelle / d'un viol. Je veux savoir IMMÉDIATEMENT : où aller pour l'examen médico-légal sans porter plainte d'abord, comment préserver les preuves, quel hôpital, et qui appeler maintenant." },
    { label: "👊 Harcèlement de rue répété", text: "Je subis du harcèlement de rue répété de [DÉCRIRE personne / groupe]. C'est la [Nème] fois. Je veux constituer un dossier de preuve et porter plainte pour harcèlement et discrimination." },
    { label: "🏠 Violence conjugale / familiale LGBTQI+", text: "Mon/ma conjoint(e) ou un membre de ma famille me bat / me menace / me séquestre à cause de mon orientation ou identité. Je dois sortir vite. Aide-moi à constituer la preuve et à trouver un hébergement d'urgence LGBTQI+." },
  ],
};

function ScenarioFlow({ scenario, working, setWorking, onDone }) {
  const meta = SCENARIO_META[scenario];
  // Bagarre defaults to video (camera+audio) for stronger evidence
  const useVideo = scenario === "bagarre" || scenario === "agression_lgbtq";
  const [tab, setTab] = useState("text");
  const [transcript, setTranscript] = useState("");
  const [context, setContext] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [geo, setGeo] = useState(null);
  const [occurredAt, setOccurredAt] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoStreamRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const autoTriggered = useRef(false);

  // Capture forensic metadata on mount: timestamp + geolocation
  useEffect(() => {
    setOccurredAt(new Date().toISOString());
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const g = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${g.lat}&lon=${g.lon}&addressdetails=1`,
            { headers: { "User-Agent": "AvocatDePoche/1.0" } })
            .then((r) => r.json())
            .then((d) => {
              if (d?.display_name) g.address = d.display_name;
              setGeo(g);
            })
            .catch(() => setGeo(g));
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
  }, []);

  const startRecording = async () => {
    try {
      const constraints = useVideo
        ? { audio: true, video: { facingMode: "environment" } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      videoStreamRef.current = stream;
      // Show live video preview if applicable
      if (useVideo && videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        try { await videoPreviewRef.current.play(); } catch {}
      }
      // Pick best supported mime type
      const mime = useVideo
        ? (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
          : MediaRecorder.isTypeSupported("video/webm") ? "video/webm"
          : "video/mp4")
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mime });
        const ext = mime.includes("video") ? "webm" : "webm";
        const f = new File([blob], `record_${Date.now()}.${ext}`, { type: mime });
        setAudioFile(f);
        stream.getTracks().forEach((t) => t.stop());
        videoStreamRef.current = null;
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error(useVideo
        ? "Impossible d'accéder à la caméra/micro — autorisez l'accès dans votre navigateur"
        : "Impossible d'accéder au micro — autorisez l'accès dans votre navigateur");
    }
  };
  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  // Auto-start recording for live scenarios when ?auto=1 is set (from Landing or AI Helper)
  useEffect(() => {
    if (autoTriggered.current) return;
    if (searchParams.get("auto") !== "1") return;
    autoTriggered.current = true;
    setTab("audio");
    // Slight delay to allow tab content to mount
    const t = setTimeout(() => {
      startRecording();
      // Clear auto param so refresh doesn't re-trigger
      searchParams.delete("auto");
      setSearchParams(searchParams, { replace: true });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      let res;
      const submittedAt = occurredAt || new Date().toISOString();
      if (tab === "text") {
        if (!transcript.trim()) { toast.error("Décrivez ce qui se passe"); setWorking(false); return; }
        res = await api.post("/sos/scenario/text", {
          scenario, transcript, context: context || null,
          geo: geo || null,
          occurred_at: submittedAt,
        });
      } else {
        if (!audioFile) { toast.error("Enregistrez ou uploadez un audio"); setWorking(false); return; }
        const fd = new FormData();
        fd.append("file", audioFile);
        fd.append("scenario", scenario);
        if (context) fd.append("context", context);
        if (geo) fd.append("geo", JSON.stringify(geo));
        fd.append("occurred_at", submittedAt);
        res = await api.post("/sos/scenario/audio", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
      }

      // Si l'IA tourne en arrière-plan (statut "processing"), on poll jusqu'à ce qu'elle finisse.
      let alertData = res.data;
      if (alertData?.status === "processing" || alertData?.parsed?._processing) {
        toast.info("Analyse en cours — patientez 1 à 2 minutes...");
        const alertId = alertData.alert_id;
        for (let i = 0; i < 60; i++) {  // max 3 minutes (60 × 3s)
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const poll = await api.get(`/sos/alerts/${alertId}`);
            if (poll.data?.status === "ready" || (poll.data?.parsed && !poll.data.parsed._processing)) {
              alertData = poll.data;
              break;
            }
            if (poll.data?.status === "error") {
              throw new Error(poll.data?.parsed?.error || "Erreur IA");
            }
          } catch (pollErr) {
            // Continue polling even on transient errors
            if (i === 59) throw pollErr;
          }
        }
      }

      toast.success("Analyse prête");
      onDone(alertData);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erreur");
    } finally { setWorking(false); }
  };

  const Icon = meta.icon;
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`scenario-form-${scenario}`}>
      <div className={`${meta.color} text-white p-6 flex items-center gap-4`}>
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">{meta.eyebrow}</p>
          <h2 className="font-serif-display text-2xl font-medium">{meta.label}</h2>
        </div>
      </div>

      <div className="p-8">
        {scenario === "agression_lgbtq" && (
          <div className="bg-rose-50 border-l-4 border-rose-700 p-4 mb-4 rounded-sm" data-testid="refuge-cta">
            <p className="text-sm font-medium text-rose-900 mb-1">🏠 Besoin d'un hébergement d'urgence ?</p>
            <p className="text-xs text-rose-800 mb-2">Un réseau d'hôtes vérifié·es ouvre sa porte aux victimes LGBTQI+ et aux femmes en danger. Anonyme · Sécurisé.</p>
            <a
              href="/refuge"
              className="inline-block text-sm font-medium text-white bg-rose-700 hover:bg-rose-800 px-3 py-1.5 rounded-sm"
              data-testid="open-refuge-link"
            >
              Ouvrir Refuge solidaire →
            </a>
          </div>
        )}
        {(geo || occurredAt) && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="forensic-info">
            {occurredAt && <span>⏰ {new Date(occurredAt).toLocaleString("fr-BE")}</span>}
            {geo?.address && <span>📍 {geo.address.split(",").slice(0, 3).join(", ")}</span>}
            {geo?.lat && !geo?.address && <span>📍 {geo.lat.toFixed(4)}, {geo.lon.toFixed(4)}</span>}
            <span className="text-slate-400">• Inscrit dans la plainte/mail comme preuve</span>
          </div>
        )}
        {recording && (
          <div className="bg-red-50 border-2 border-red-600 rounded-sm p-4 mb-6 animate-fade-in-up" data-testid="recording-banner">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 animate-pulse">
                <Mic className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-red-700 uppercase tracking-wider">
                  🔴 {useVideo ? "Caméra + micro" : "Enregistrement"} en cours · Preuve en train d'être collectée
                </p>
                <p className="text-xs text-red-600 mt-1">Restez calme. Continuez à parler / faire parler. L'app capture tout.</p>
              </div>
              <Button type="button" onClick={stopRecording} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm h-9" data-testid={`${scenario}-banner-stop`}>
                <MicOff className="h-3.5 w-3.5 mr-1.5" /> Arrêter
              </Button>
            </div>
            {useVideo && (
              <div className="mt-4 rounded-sm overflow-hidden bg-black border border-red-600">
                <video
                  ref={videoPreviewRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full max-h-72 object-contain bg-black"
                  data-testid="video-preview"
                />
              </div>
            )}
          </div>
        )}
        {audioFile && !recording && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-6 flex items-center gap-2" data-testid="recording-saved-banner">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <p className="text-sm text-emerald-800">
              ✓ {audioFile.type?.startsWith("video") ? "Vidéo" : "Preuve audio"} enregistré ({(audioFile.size / 1024 / 1024).toFixed(2)} MB) — touchez "Analyser" ci-dessous pour préparer le mail au supérieur
            </p>
          </div>
        )}

        <p className="text-sm text-slate-600 mb-6">{meta.desc}</p>

        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList className="rounded-sm grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="text" className="rounded-sm" data-testid={`${scenario}-tab-text`}>
              <FileText className="h-3.5 w-3.5 mr-2" /> Texte
            </TabsTrigger>
            <TabsTrigger value="audio" className="rounded-sm" data-testid={`${scenario}-tab-audio`}>
              <Mic className="h-3.5 w-3.5 mr-2" /> Audio
            </TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="mt-6">
            <Label className="text-xs uppercase tracking-widest mb-2 block">Ce qui se dit / se passe</Label>
            <div className="relative">
              <Textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={6}
                placeholder={scenario === "police" ? "L'agent me dit d'ouvrir mon sac sans motif, refuse de montrer sa carte..."
                  : scenario === "bagarre" ? "Mon voisin m'insulte devant témoins, me bouscule devant sa porte..."
                  : "Le notaire veut me faire signer un acte avec une clause de 5000€ de frais non prévue au devis..."}
                className="rounded-sm pr-12"
                data-testid={`${scenario}-transcript`}
              />
              <div className="absolute top-2 right-2">
                <VoiceMicButton
                  currentValue={transcript}
                  onTranscript={setTranscript}
                  testid={`${scenario}-voice-mic`}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">🎙️ Touchez le micro pour dicter à la place de taper</p>

            {SCENARIO_TEMPLATES[scenario] && (
              <div className="mt-5 pt-5 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-3">⚡ Modèles rapides — 1 tap pour pré-remplir</p>
                <div className="flex flex-wrap gap-2">
                  {SCENARIO_TEMPLATES[scenario].map((tpl, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setTranscript(tpl.text)}
                      className="text-xs bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 rounded-sm px-3 py-2 transition-colors text-left"
                      data-testid={`${scenario}-template-${i}`}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="audio" className="mt-6">
            <Label className="text-xs uppercase tracking-widest mb-3 block">
              {useVideo ? "Enregistrement vidéo (caméra + son)" : "Enregistrement audio"}
            </Label>
            <div className="border border-slate-200 rounded-sm p-6 bg-slate-50">
              {audioFile ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mic className="h-5 w-5 text-[#B89C65]" />
                    <div>
                      <p className="text-sm font-medium">{audioFile.name}</p>
                      <p className="text-xs text-slate-500">{(audioFile.size / 1024 / 1024).toFixed(2)} MB · {audioFile.type?.startsWith("video") ? "Vidéo" : "Audio"}</p>
                    </div>
                  </div>
                  <Button type="button" onClick={() => setAudioFile(null)} variant="ghost" className="rounded-sm text-red-600" data-testid={`${scenario}-audio-clear`}>
                    Effacer
                  </Button>
                </div>
              ) : recording ? null : (
                <div className="text-center">
                  <Button type="button" onClick={startRecording} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid={`${scenario}-audio-start`}>
                    {useVideo ? <Camera className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                    {useVideo ? "Démarrer caméra + micro" : "Commencer l'enregistrement"}
                  </Button>
                  <p className="text-xs text-slate-500 mt-3">
                    {useVideo
                      ? "Vidéo + son captés simultanément (preuve maximale)"
                      : "Ou téléchargez un fichier audio existant ↓"}
                  </p>
                  {!useVideo && (
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      className="block mx-auto mt-2 text-xs"
                      data-testid={`${scenario}-audio-upload`}
                    />
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mb-6">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Contexte additionnel (optionnel)</Label>
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={2}
            placeholder="Lieu, heure, parties impliquées..."
            className="rounded-sm"
            data-testid={`${scenario}-context`}
          />
        </div>

        <Button
          type="submit"
          disabled={working}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8 w-full sm:w-auto"
          data-testid={`${scenario}-submit`}
        >
          {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse...</> : <><Icon className="h-4 w-4 mr-2" />Analyser et préparer la défense</>}
        </Button>
      </div>
    </form>
  );
}

// ============ ANTI-ARNAQUE : MESSAGE (SMS / Email / Facture) ============
function ScamMessageFlow({ working, setWorking, onDone }) {
  const [channel, setChannel] = useState("sms");
  const [sender, setSender] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const addFiles = (selected) => {
    if (!selected) return;
    const arr = Array.from(selected);
    const valid = arr.filter((f) => {
      const ok = ["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type);
      if (!ok) toast.error(`Format refusé : ${f.name}`);
      return ok;
    });
    setFiles((prev) => [...prev, ...valid].slice(0, 8));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && files.length === 0) {
      toast.error("Collez le message ou joignez une capture / facture");
      return;
    }
    setWorking(true);
    try {
      const fd = new FormData();
      if (body.trim()) fd.append("body", body);
      if (subject.trim()) fd.append("subject", subject);
      if (sender.trim()) fd.append("sender", sender);
      fd.append("channel", channel);
      files.forEach((f) => fd.append("files", f));
      const res = await api.post("/sos/scam-check/message", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("Analyse anti-arnaque prête");
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setWorking(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="scam-message-form">
      <div className="bg-amber-600 text-white p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <MessageSquareWarning className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 5 · Anti-arnaque</p>
          <h2 className="font-serif-display text-2xl font-medium">Message suspect — SMS, email, facture</h2>
        </div>
      </div>

      <div className="p-8">
        <p className="text-sm text-slate-600 mb-6">
          Vous avez reçu un SMS du « SPF Finances », un email d'huissier, une facture étrange ?
          Collez le texte ou prenez une photo : l'IA détecte le phishing belge le plus courant
          (faux Bpost, faux huissier, smishing bancaire, faux fournisseur d'énergie).
        </p>

        <div className="mb-5">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Canal</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "sms", l: "SMS" },
              { v: "email", l: "Email" },
              { v: "facture", l: "Facture / Courrier" },
              { v: "whatsapp", l: "WhatsApp" },
              { v: "autre", l: "Autre" },
            ].map((c) => (
              <button
                type="button"
                key={c.v}
                onClick={() => setChannel(c.v)}
                className={`text-xs rounded-sm px-3 py-2 border transition-colors ${
                  channel === c.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                }`}
                data-testid={`scam-channel-${c.v}`}
              >
                {c.l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Expéditeur (optionnel)</Label>
            <Input
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="0470/xx.xx.xx ou no-reply@bpost-update.com"
              className="rounded-sm"
              data-testid="scam-sender"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Objet (optionnel)</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Frais de douane impayés…"
              className="rounded-sm"
              data-testid="scam-subject"
            />
          </div>
        </div>

        <div className="mb-5">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Contenu du message (collez ici)</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Bpost : votre colis est bloqué, payez 2,99€ ici : https://bpost-secure-pay.xyz/…"
            className="rounded-sm font-mono text-sm"
            data-testid="scam-body"
          />
        </div>

        <div className="mb-6">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Captures d'écran / factures (optionnel)</Label>
          <div
            className="border border-dashed border-slate-300 rounded-sm p-6 text-center cursor-pointer hover:border-slate-900 hover:bg-slate-50 transition-all"
            onClick={() => fileRef.current?.click()}
            data-testid="scam-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
              data-testid="scam-file-input"
            />
            <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-700">Joindre image(s) ou PDF</p>
            <p className="text-xs text-slate-500 mt-1">Capture de SMS, screenshot d'email, facture · max 20MB / fichier</p>
          </div>

          {files.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="scam-files-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-[#B89C65] flex-shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{f.name}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600" data-testid={`scam-file-remove-${i}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={working}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8 w-full sm:w-auto"
          data-testid="scam-message-submit"
        >
          {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse en cours...</> : <><ShieldCheck className="h-4 w-4 mr-2" />Vérifier si c'est une arnaque</>}
        </Button>
      </div>
    </form>
  );
}

// ============ ANTI-ARNAQUE : VOIX (Deepfake / faux proche) ============
function ScamVoiceFlow({ working, setWorking, onDone }) {
  const [tab, setTab] = useState("record");
  const [audioFile, setAudioFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [suspectedCaller, setSuspectedCaller] = useState("");
  const [context, setContext] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mime });
        const f = new File([blob], `call_${Date.now()}.webm`, { type: mime });
        setAudioFile(f);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Impossible d'accéder au micro — autorisez l'accès dans votre navigateur");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!audioFile) { toast.error("Enregistrez ou uploadez l'appel suspect"); return; }
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", audioFile);
      if (suspectedCaller.trim()) fd.append("suspected_caller", suspectedCaller);
      if (context.trim()) fd.append("context", context);
      const res = await api.post("/sos/scam-check/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("Analyse vocale prête");
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setWorking(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="scam-voice-form">
      <div className="bg-violet-700 text-white p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <PhoneCall className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 6 · Anti-arnaque</p>
          <h2 className="font-serif-display text-2xl font-medium">Appel suspect — voix clonée / faux proche</h2>
        </div>
      </div>

      <div className="p-8">
        <p className="text-sm text-slate-600 mb-6">
          « Maman, c'est moi, j'ai eu un accident, envoie-moi 800€ vite… » — l'IA peut cloner
          n'importe quelle voix en 3 secondes. Faites-nous écouter l'appel (ou enregistrez en direct
          si le ravisseur reste en ligne) : on détecte l'arnaque et on vous donne les questions à poser.
        </p>

        {recording && (
          <div className="bg-red-50 border-2 border-red-600 rounded-sm p-4 mb-6 animate-fade-in-up" data-testid="scam-voice-recording">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 animate-pulse">
                <Mic className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-red-700 uppercase tracking-wider">🔴 Enregistrement en cours</p>
                <p className="text-xs text-red-600 mt-1">Mettez l'appel en haut-parleur. Restez calme. Posez les questions de vérification.</p>
              </div>
              <Button type="button" onClick={stopRecording} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm h-9" data-testid="scam-voice-stop">
                <MicOff className="h-3.5 w-3.5 mr-1.5" /> Arrêter
              </Button>
            </div>
          </div>
        )}

        {audioFile && !recording && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-6 flex items-center justify-between" data-testid="scam-voice-saved">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-emerald-700" />
              <span className="text-sm text-emerald-800">
                ✓ {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB) — prêt pour analyse
              </span>
            </div>
            <Button type="button" variant="ghost" onClick={() => setAudioFile(null)} className="rounded-sm text-red-600 text-xs h-8" data-testid="scam-voice-clear">
              Effacer
            </Button>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList className="rounded-sm grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="record" className="rounded-sm" data-testid="scam-voice-tab-record">
              <Mic className="h-3.5 w-3.5 mr-2" /> Enregistrer
            </TabsTrigger>
            <TabsTrigger value="upload" className="rounded-sm" data-testid="scam-voice-tab-upload">
              <Upload className="h-3.5 w-3.5 mr-2" /> Importer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="record" className="mt-6">
            <div className="border border-slate-200 rounded-sm p-6 bg-slate-50 text-center">
              {!recording && !audioFile && (
                <>
                  <Button type="button" onClick={startRecording} className="rounded-sm bg-red-600 hover:bg-red-700 h-11 px-6" data-testid="scam-voice-start">
                    <Mic className="h-4 w-4 mr-2" /> Commencer à enregistrer l'appel
                  </Button>
                  <p className="text-xs text-slate-500 mt-3">
                    Activez le haut-parleur du téléphone. L'app capture l'audio via le micro de votre appareil.
                  </p>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-6">
            <div className="border border-slate-200 rounded-sm p-6 bg-slate-50 text-center">
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-3" />
              <input
                type="file"
                accept="audio/*,video/mp4,video/webm"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                className="block mx-auto text-xs"
                data-testid="scam-voice-upload"
              />
              <p className="text-xs text-slate-500 mt-3">
                Fichier messagerie vocale, mémo audio, ou enregistrement d'appel · MP3 / WAV / M4A / WEBM · max 25MB
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">L'appelant prétend être (optionnel)</Label>
            <Input
              value={suspectedCaller}
              onChange={(e) => setSuspectedCaller(e.target.value)}
              placeholder="Mon fils Pierre / Mon banquier ING…"
              className="rounded-sm"
              data-testid="scam-voice-caller"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">Contexte (optionnel)</Label>
            <Input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Demande 800€ urgent, refuse appel vidéo…"
              className="rounded-sm"
              data-testid="scam-voice-context"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={working || !audioFile}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8 w-full sm:w-auto"
          data-testid="scam-voice-submit"
        >
          {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transcription + analyse...</> : <><ShieldCheck className="h-4 w-4 mr-2" />Détecter le deepfake / arnaque</>}
        </Button>
      </div>
    </form>
  );
}

// ============ TOUCHE 12 — MEDICAL HUB (dossier + QR + vérification) ============
function MedicalHub({ working, setWorking, onScenarioDone }) {
  const [tab, setTab] = useState("dossier"); // dossier | qr | verifier
  const [record, setRecord] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/medical/record");
        setRecord(r.data?.record || null);
        setShareToken(r.data?.share_token || null);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="bg-white p-8 rounded-sm flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="medical-hub">
      <div className="bg-cyan-800 text-white p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <HeartPulse className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 12 · Santé</p>
          <h2 className="text-xl font-serif-display">Médical / Hôpital — Dossier d'urgence</h2>
        </div>
      </div>

      {/* Sous-onglets */}
      <div className="flex flex-wrap border-b border-slate-200 bg-cyan-50">
        {[
          { id: "dossier", label: "📋 Mon dossier", icon: FileText },
          { id: "qr", label: "🔲 QR urgence", icon: QrCode },
          { id: "verifier", label: "💊 Vérifier", icon: Pill },
          { id: "consultation", label: "🩺 Consultation sécurisée", icon: HeartPulse },
          { id: "silent_sos", label: "🆘 SOS muet", icon: ShieldAlert },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 min-w-[110px] py-3 text-xs sm:text-sm font-medium transition ${
              tab === t.id ? "bg-white text-cyan-900 border-b-2 border-cyan-800" : "text-cyan-700 hover:bg-cyan-100"
            } ${t.id === "silent_sos" ? "text-red-700 hover:bg-red-50" : ""}`}
            data-testid={`medical-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "dossier" && (
        <MedicalRecordForm
          record={record}
          working={working} setWorking={setWorking}
          onSaved={(r, token) => { setRecord(r); setShareToken(token); toast.success("Dossier enregistré"); }}
        />
      )}

      {tab === "qr" && (
        <div className="p-6 space-y-4 text-center" data-testid="medical-qr-panel">
          {!record ? (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 text-sm text-amber-900">
              📋 Remplissez d'abord votre dossier dans l'onglet "Mon dossier" pour générer un QR code.
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-700">
                Imprimez et collez ce QR code dans votre <strong>portefeuille / coque de téléphone</strong>.
                En cas d'urgence, n'importe quel médecin/urgentiste pourra le scanner pour voir vos allergies et traitements.
              </p>
              <div className="flex justify-center">
                <img
                  src={`${apiUrl}/api/medical/qr.png`}
                  alt="QR code dossier médical"
                  className="border-4 border-cyan-800 p-3 bg-white"
                  style={{ maxWidth: 280 }}
                  data-testid="medical-qr-img"
                />
              </div>
              <p className="text-xs text-slate-500">
                Lien de partage (lecture seule, sans authentification) : <br/>
                <code className="text-[10px] break-all bg-slate-100 px-1">/urgence/{shareToken}</code>
              </p>
              <a
                href={`${apiUrl}/api/medical/qr.png`}
                download="qr-urgence-avocat-en-or.png"
                className="inline-block bg-cyan-800 text-white px-5 py-2 rounded-sm text-sm hover:bg-cyan-900 mr-2"
                data-testid="medical-qr-download"
              >
                📥 Télécharger le QR seul (PNG)
              </a>
              <a
                href={`${apiUrl}/api/medical/card.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-rose-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-rose-800"
                data-testid="medical-card-download"
              >
                💳 Carte format portefeuille (PDF)
              </a>
            </>
          )}
        </div>
      )}

      {tab === "verifier" && (
        <div className="border-t border-slate-200">
          <ScenarioFlow
            scenario="medical"
            working={working} setWorking={setWorking}
            onDone={onScenarioDone}
          />
        </div>
      )}

      {tab === "consultation" && (
        <SecureConsultation record={record} />
      )}

      {tab === "silent_sos" && (
        <SilentSOSPanel record={record} shareToken={shareToken} apiUrl={apiUrl} />
      )}
    </div>
  );
}

// ============ CONSULTATION SÉCURISÉE — micro live + alerte allergies ============
// Utilisé avec l'accord du médecin lors d'une prescription. Écoute et transcrit en direct.
// Compare chaque mot en temps réel à la liste des allergies/contre-indications du dossier.
// Si match → VIBRATION forte + popup d'alerte.
function SecureConsultation({ record }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [alerts, setAlerts] = useState([]); // [{term, detectedAt}]
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupTerm, setPopupTerm] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const alertedTermsRef = useRef(new Set());

  // Normalise : minuscules + supprime accents
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Liste combinée des termes à surveiller (allergies + composants dangereux courants)
  const watchList = useMemo(() => {
    const list = [];
    (record?.allergies || []).forEach((a) => list.push({ term: a, kind: "allergie" }));
    (record?.contraindications || []).forEach((c) => list.push({ term: c, kind: "contre-indication" }));
    (record?.conditions || []).forEach((c) => list.push({ term: c, kind: "condition" }));
    return list
      .filter((x) => x.term && x.term.trim().length > 2)
      .map((x) => ({ ...x, normTerm: norm(x.term) }));
  }, [record]);

  // Vérifie un texte transcrit contre la watchlist
  const scan = (text) => {
    const nText = " " + norm(text) + " ";
    for (const w of watchList) {
      const nT = " " + w.normTerm + " ";
      const nT2 = w.normTerm; // fallback pour composés
      if ((nText.includes(nT) || nText.includes(nT2)) && !alertedTermsRef.current.has(w.normTerm)) {
        alertedTermsRef.current.add(w.normTerm);
        triggerAlert(w);
      }
    }
  };

  const triggerAlert = (w) => {
    // Vibration forte (motif long si supporté)
    try {
      if (navigator.vibrate) {
        navigator.vibrate([500, 200, 500, 200, 500, 200, 500]);
      }
    } catch { /* ignore */ }
    // Son court (bip via AudioContext)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close(); }, 400);
    } catch { /* ignore */ }
    // TTS parlée
    try {
      const u = new SpeechSynthesisUtterance(`Attention, risque d'allergie détecté : ${w.term}`);
      u.lang = "fr-FR"; u.rate = 1.1;
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
    setPopupTerm(w.term);
    setPopupOpen(true);
    setAlerts((a) => [...a, { term: w.term, kind: w.kind, at: new Date().toISOString() }]);
  };

  const start = () => {
    setError(null);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      setError("Votre navigateur ne supporte pas la reconnaissance vocale en direct. Utilisez Chrome ou Safari sur mobile.");
      return;
    }
    if (watchList.length === 0) {
      setError("Votre dossier médical est vide. Remplissez au moins vos allergies dans l'onglet 'Mon dossier' avant de commencer.");
      return;
    }
    const r = new SR();
    r.lang = "fr-FR";
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (ev) => {
      let finalText = "";
      let interimText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const seg = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += seg + " ";
        else interimText += seg + " ";
      }
      if (finalText) {
        setTranscript((prev) => prev + finalText);
        scan(finalText);
      }
      if (interimText) {
        setInterim(interimText);
        scan(interimText);
      }
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Micro refusé. Autorisez le micro dans les paramètres du navigateur.");
      } else if (e.error !== "no-speech" && e.error !== "aborted") {
        setError(`Erreur reconnaissance : ${e.error}`);
      }
    };
    r.onend = () => {
      // Auto-reprendre si toujours en écoute (Chrome coupe après ~60s)
      if (recognitionRef.current === r && listening) {
        try { r.start(); } catch { /* déjà démarré */ }
      }
    };
    try {
      r.start();
      recognitionRef.current = r;
      setListening(true);
    } catch (e) {
      setError("Impossible de démarrer le micro : " + e.message);
    }
  };

  const stop = () => {
    setListening(false);
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    recognitionRef.current = null;
  };

  const reset = () => {
    setTranscript(""); setInterim(""); setAlerts([]);
    alertedTermsRef.current = new Set();
  };

  useEffect(() => () => {
    // Cleanup si unmount pendant écoute
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="secure-consultation">
      {/* Bandeau explicatif */}
      <div className="bg-blue-50 border-l-4 border-blue-700 p-4 rounded-sm">
        <p className="text-sm font-bold text-blue-900 mb-1">🩺 Consultation sécurisée — avec l'accord de votre médecin</p>
        <p className="text-xs text-blue-800 leading-relaxed">
          Activez cette option AU MOMENT de la prescription. L'app écoute le médecin en direct.
          Si le nom d'un composant auquel vous êtes allergique est prononcé,
          <strong> votre téléphone vibre immédiatement et vous alerte</strong> — pour que vous puissiez signaler le risque au médecin avant qu'il ne signe l'ordonnance.
        </p>
      </div>

      {/* État du dossier */}
      {watchList.length === 0 ? (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-sm text-amber-900" data-testid="consultation-empty-record">
          ⚠️ Aucune allergie renseignée. Allez d'abord dans l'onglet <strong>Mon dossier</strong> pour saisir vos allergies et contre-indications.
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700" data-testid="consultation-watchlist">
          <p className="font-bold text-slate-800 mb-1">🎯 Termes surveillés en direct :</p>
          <div className="flex flex-wrap gap-1.5">
            {watchList.map((w, i) => (
              <span key={i} className={`px-2 py-0.5 rounded-sm text-[11px] border ${w.kind === "allergie" ? "bg-red-50 border-red-300 text-red-800" : w.kind === "contre-indication" ? "bg-orange-50 border-orange-300 text-orange-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}>
                {w.term}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Erreur éventuelle */}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-3 text-sm text-red-800" data-testid="consultation-error">
          {error}
        </div>
      )}

      {/* Contrôles */}
      <div className="flex gap-3">
        {!listening ? (
          <button
            type="button"
            onClick={start}
            disabled={!supported || watchList.length === 0}
            className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 rounded-sm shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition"
            data-testid="consultation-start-btn"
          >
            🎙️ Démarrer l'écoute
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={stop}
              className="flex-1 bg-red-700 hover:bg-red-800 text-white font-bold py-4 rounded-sm shadow-lg animate-pulse"
              data-testid="consultation-stop-btn"
            >
              ⏹️ Arrêter
            </button>
            <button
              type="button"
              onClick={() => { navigator.vibrate?.(300); }}
              className="px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm text-xs"
              data-testid="consultation-vibrate-test"
              title="Tester la vibration"
            >📳</button>
          </>
        )}
      </div>

      {/* Statut live */}
      {listening && (
        <div className="bg-red-50 border-2 border-red-600 rounded-sm p-3 flex items-center gap-2" data-testid="consultation-live">
          <span className="h-3 w-3 rounded-full bg-red-600 animate-pulse" />
          <p className="text-sm font-medium text-red-800">Écoute en direct… gardez le téléphone visible ou vibrant.</p>
        </div>
      )}

      {/* Alertes déclenchées */}
      {alerts.length > 0 && (
        <div className="bg-white border-2 border-red-600 rounded-sm p-3 space-y-2" data-testid="consultation-alerts">
          <p className="text-sm font-bold text-red-700">⚠️ {alerts.length} alerte{alerts.length > 1 ? "s" : ""} déclenchée{alerts.length > 1 ? "s" : ""}</p>
          {alerts.map((a, i) => (
            <div key={i} className="bg-red-50 border border-red-300 rounded-sm p-2 text-sm text-red-900 flex items-center justify-between" data-testid={`consultation-alert-${i}`}>
              <span><strong>{a.term}</strong> <span className="text-[11px] text-red-700">({a.kind})</span></span>
              <span className="text-[10px] text-red-600">{new Date(a.at).toLocaleTimeString("fr-BE")}</span>
            </div>
          ))}
        </div>
      )}

      {/* Transcription live */}
      {(transcript || interim) && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="consultation-transcript">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">📝 Transcription</p>
          <p className="text-sm text-slate-800 whitespace-pre-wrap">
            {transcript}
            <span className="text-slate-400 italic">{interim}</span>
          </p>
        </div>
      )}

      {(transcript || alerts.length > 0) && !listening && (
        <button
          type="button"
          onClick={reset}
          className="text-xs text-slate-600 underline hover:text-slate-900"
          data-testid="consultation-reset-btn"
        >
          🧹 Effacer la session
        </button>
      )}

      {/* Popup ALERTE FORTE */}
      {popupOpen && (
        <div className="fixed inset-0 z-[100] bg-red-900/80 flex items-center justify-center p-4" data-testid="consultation-alert-popup" role="alertdialog">
          <div className="bg-white border-8 border-red-600 rounded-sm max-w-md w-full p-6 shadow-2xl animate-pulse">
            <div className="text-5xl text-center mb-3">⚠️</div>
            <h3 className="font-serif-display text-2xl text-red-700 text-center mb-2">Attention !</h3>
            <p className="text-lg text-red-900 text-center mb-4">
              Risque d'allergie détecté pour ce composant :
            </p>
            <p className="text-3xl font-bold text-red-700 text-center bg-red-50 border-2 border-red-300 rounded-sm py-3 mb-4">
              {popupTerm}
            </p>
            <p className="text-sm text-slate-700 text-center mb-4">
              Signalez immédiatement ce risque à votre médecin avant qu'il ne signe l'ordonnance.
            </p>
            <button
              type="button"
              onClick={() => setPopupOpen(false)}
              className="w-full bg-red-700 hover:bg-red-800 text-white font-bold py-3 rounded-sm"
              data-testid="consultation-popup-close"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ SOS MUET — affichage plein écran pour personnes incapables de parler ============
function SilentSOSPanel({ record, shareToken, apiUrl }) {
  const [geo, setGeo] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [emergencyMode, setEmergencyMode] = useState(false);

  // Active GPS dès qu'on entre en mode urgence
  useEffect(() => {
    if (!emergencyMode) return;
    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n'est pas disponible sur cet appareil.");
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        // Reverse geocoding optionnel via backend
        let address = null;
        try {
          const r = await api.get(`/geo/detect?lat=${latitude}&lon=${longitude}`);
          address = r.data?.address || null;
        } catch (_) {}
        setGeo({ lat: latitude, lon: longitude, accuracy, address });
      },
      (err) => setGeoError(err.message || "GPS refusé"),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [emergencyMode]);

  if (!record) {
    return (
      <div className="p-6 bg-amber-50 border border-amber-300 text-amber-900 text-sm" data-testid="silent-sos-no-record">
        📋 Remplissez d'abord votre <strong>dossier médical</strong> dans l'onglet "Mon dossier" pour que le mode SOS muet puisse afficher vos infos vitales.
      </div>
    );
  }

  if (!emergencyMode) {
    return (
      <div className="p-6 space-y-4 text-center" data-testid="silent-sos-intro">
        <div className="bg-red-50 border-2 border-red-700 rounded-sm p-5 text-left">
          <h3 className="font-serif-display text-xl font-bold text-red-700 mb-2">🆘 Mode SOS muet — Comment ça marche ?</h3>
          <p className="text-sm text-slate-800 mb-2">
            Vous ne pouvez pas parler (AVC, crise d'asthme, choc, sourd, traumatisme) ?
            Touchez le bouton ci-dessous : votre téléphone affichera <strong>en GRAND</strong>
            votre situation, vos allergies, vos traitements et votre position GPS.
          </p>
          <p className="text-sm text-slate-800 mb-2">
            <strong>Vous pouvez aussi tendre votre téléphone à n'importe quel passant ou ambulancier</strong>
            — toutes les infos critiques pour vous sauver seront visibles d'un coup d'œil.
          </p>
          <p className="text-xs text-slate-600 italic">
            ⚠️ Limite technique : aucune application ne peut composer le 112 toute seule. Mais ce mode pré-remplit le 112 d'un seul tap et affiche tout pour les secours.
          </p>
        </div>

        <button
          onClick={() => setEmergencyMode(true)}
          className="w-full bg-red-700 hover:bg-red-800 active:bg-red-900 text-white font-bold text-xl py-8 rounded-sm shadow-2xl ring-4 ring-red-300 ring-offset-2 transition-transform active:scale-95"
          data-testid="silent-sos-activate-btn"
        >
          🆘 ACTIVER LE MODE SOS MUET 🆘
        </button>

        <p className="text-xs text-slate-500">
          Astuce : ajoutez ce mode à votre écran d'accueil pour y accéder en 1 tap en cas d'urgence.
        </p>
      </div>
    );
  }

  // === MODE URGENCE PLEIN ÉCRAN ===
  const allergiesStr = (record.allergies || []).join(", ") || "Aucune connue";
  const medsStr = (record.current_meds || []).join(", ") || "Aucun";
  const conditionsStr = (record.conditions || []).join(", ") || "Aucune";
  const bloodStr = record.blood_type || "?";
  const contactStr = record.emergency_contact || null;
  const shareUrl = shareToken ? `${apiUrl.replace("/api", "")}/urgence/${shareToken}` : null;

  // Pré-remplir SMS au 112 (fonctionne sur iOS/Android EU)
  const buildSmsBody = () => {
    let body = "URGENCE MEDICALE - Je ne peux pas parler.\n";
    body += `Sang: ${bloodStr}\n`;
    body += `Allergies: ${allergiesStr}\n`;
    body += `Traitements: ${medsStr}\n`;
    body += `Conditions: ${conditionsStr}\n`;
    if (geo) {
      body += `\nPosition GPS: ${geo.lat.toFixed(5)}, ${geo.lon.toFixed(5)}`;
      if (geo.address) body += `\nAdresse: ${geo.address}`;
      body += `\nMaps: https://maps.google.com/?q=${geo.lat},${geo.lon}`;
    }
    if (shareUrl) body += `\nDossier complet: ${shareUrl}`;
    return encodeURIComponent(body);
  };

  return (
    <div className="bg-red-700 text-white p-4 sm:p-6 space-y-4 min-h-screen" data-testid="silent-sos-active">
      <div className="text-center bg-white text-red-700 rounded-sm py-3 ring-4 ring-yellow-300 animate-pulse">
        <p className="text-2xl sm:text-3xl font-black tracking-tight">🆘 URGENCE MÉDICALE 🆘</p>
        <p className="text-sm sm:text-base font-bold mt-1">JE NE PEUX PAS PARLER</p>
      </div>

      {/* Bouton 112 - taille XXL */}
      <a
        href="tel:112"
        className="block w-full bg-yellow-400 hover:bg-yellow-300 text-red-900 font-black text-3xl sm:text-4xl text-center py-6 sm:py-8 rounded-sm shadow-2xl border-4 border-white"
        data-testid="silent-sos-call-112"
      >
        📞 APPELER LE 112
      </a>

      {/* Bouton SMS au 112 (pour sourds/muets) */}
      <a
        href={`sms:112?body=${buildSmsBody()}`}
        className="block w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg text-center py-4 rounded-sm shadow-xl border-2 border-white"
        data-testid="silent-sos-sms-112"
      >
        💬 ENVOYER SMS AU 112 (sourds/muets)
      </a>

      {/* Position GPS */}
      <div className="bg-white text-slate-900 rounded-sm p-4 ring-2 ring-yellow-300">
        <p className="text-xs uppercase tracking-[0.2em] text-red-700 font-bold mb-1">📍 MA POSITION</p>
        {geo ? (
          <>
            <p className="text-base font-bold leading-tight">{geo.address || "Localisé"}</p>
            <p className="text-xs text-slate-600 mt-1">
              {geo.lat.toFixed(6)}, {geo.lon.toFixed(6)}
              {geo.accuracy ? ` (±${Math.round(geo.accuracy)}m)` : ""}
            </p>
            <a
              href={`https://maps.google.com/?q=${geo.lat},${geo.lon}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-block mt-2 text-sm text-blue-700 underline"
              data-testid="silent-sos-maps-link"
            >Ouvrir dans Google Maps →</a>
          </>
        ) : geoError ? (
          <p className="text-sm text-red-700">⚠️ {geoError}. Autorisez le GPS dans les paramètres.</p>
        ) : (
          <p className="text-sm">📡 Recherche GPS en cours...</p>
        )}
      </div>

      {/* Infos médicales — GROS texte pour ambulanciers/passants */}
      <div className="bg-white text-slate-900 rounded-sm p-4 space-y-3 ring-2 ring-yellow-300" data-testid="silent-sos-medical-info">
        <p className="text-xs uppercase tracking-[0.2em] text-red-700 font-bold">🩸 INFOS MÉDICALES VITALES</p>

        <div className="border-b border-slate-200 pb-2">
          <p className="text-xs text-slate-500 uppercase">Groupe sanguin</p>
          <p className="text-3xl font-black text-red-700">{bloodStr}</p>
        </div>

        <div className="border-b border-slate-200 pb-2">
          <p className="text-xs text-slate-500 uppercase">⚠️ ALLERGIES</p>
          <p className="text-lg font-bold text-red-700 leading-tight">{allergiesStr}</p>
        </div>

        <div className="border-b border-slate-200 pb-2">
          <p className="text-xs text-slate-500 uppercase">💊 TRAITEMENTS EN COURS</p>
          <p className="text-base font-bold leading-tight">{medsStr}</p>
        </div>

        <div className="border-b border-slate-200 pb-2">
          <p className="text-xs text-slate-500 uppercase">🩺 CONDITIONS</p>
          <p className="text-base font-bold leading-tight">{conditionsStr}</p>
        </div>

        {contactStr && (
          <div className="pt-1">
            <p className="text-xs text-slate-500 uppercase">📞 PROCHE À PRÉVENIR</p>
            <p className="text-base font-bold leading-tight">{contactStr}</p>
          </div>
        )}
      </div>

      {/* QR code pour scanner par ambulanciers */}
      {shareToken && (
        <div className="bg-white rounded-sm p-4 text-center ring-2 ring-yellow-300">
          <p className="text-xs uppercase tracking-[0.2em] text-red-700 font-bold mb-2">📱 SCANNER QR — DOSSIER COMPLET</p>
          <img
            src={`${apiUrl}/api/medical/qr.png`}
            alt="QR dossier médical"
            className="mx-auto border-2 border-slate-300"
            style={{ maxWidth: 200 }}
            data-testid="silent-sos-qr"
          />
        </div>
      )}

      <button
        onClick={() => setEmergencyMode(false)}
        className="w-full bg-slate-200 text-slate-700 py-2 rounded-sm text-sm"
        data-testid="silent-sos-deactivate"
      >Quitter le mode SOS muet</button>
    </div>
  );
}

function MedicalRecordForm({ record, working, setWorking, onSaved }) {
  const [bloodType, setBloodType] = useState(record?.blood_type || "");
  const [allergies, setAllergies] = useState((record?.allergies || []).join(", "));
  const [meds, setMeds] = useState((record?.current_meds || []).join(", "));
  const [conditions, setConditions] = useState((record?.conditions || []).join(", "));
  const [contact, setContact] = useState(record?.emergency_contact || "");
  const [languages, setLanguages] = useState((record?.languages_spoken || []).join(", "));
  const [organDonor, setOrganDonor] = useState(record?.organ_donor || false);
  const [notes, setNotes] = useState(record?.notes || "");

  const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      const body = {
        blood_type: bloodType.trim() || null,
        allergies: splitList(allergies),
        current_meds: splitList(meds),
        conditions: splitList(conditions),
        emergency_contact: contact.trim() || null,
        languages_spoken: splitList(languages),
        organ_donor: organDonor,
        notes: notes.trim() || null,
      };
      const res = await api.post("/medical/record", body);
      onSaved(body, res.data?.share_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setWorking(false); }
  };

  return (
    <form onSubmit={save} className="p-6 space-y-4" data-testid="medical-record-form">
      <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-3 text-xs text-cyan-900">
        <strong>🔒 Confidentialité :</strong> seules les informations vitales (allergies, traitements, sang) seront visibles via le QR code.
        Les notes restent privées et accessibles uniquement à vous.
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Groupe sanguin</label>
        <select value={bloodType} onChange={(e) => setBloodType(e.target.value)}
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-blood">
          <option value="">— Inconnu —</option>
          {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Allergies (séparer par des virgules)</label>
        <input type="text" value={allergies} onChange={(e) => setAllergies(e.target.value)}
          placeholder="Ex: pénicilline, arachides, latex, ibuprofène"
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-allergies" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Traitements en cours (séparer par des virgules)</label>
        <input type="text" value={meds} onChange={(e) => setMeds(e.target.value)}
          placeholder="Ex: Levothyrox 50µg, Doliprane 1000mg, Ventoline"
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-current" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Conditions chroniques</label>
        <input type="text" value={conditions} onChange={(e) => setConditions(e.target.value)}
          placeholder="Ex: diabète type 2, hypertension, asthme"
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-conditions" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Contact d'urgence</label>
        <input type="text" value={contact} onChange={(e) => setContact(e.target.value)}
          placeholder="Ex: Marie Dupont, épouse, +32 487 12 34 56"
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-emergency" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Langues parlées</label>
        <input type="text" value={languages} onChange={(e) => setLanguages(e.target.value)}
          placeholder="Ex: français, arabe, anglais"
          className="w-full border border-slate-300 rounded-sm p-2 text-sm" data-testid="med-langs" />
      </div>
      <div className="flex items-center gap-3">
        <input type="checkbox" id="med-donor" checked={organDonor} onChange={(e) => setOrganDonor(e.target.checked)} data-testid="med-donor" />
        <label htmlFor="med-donor" className="text-sm text-slate-700">Je suis donneur d'organes</label>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-1">Notes privées (non visibles via QR)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: pacemaker depuis 2022, suivi cardio Dr X tel..."
          className="w-full border border-slate-300 rounded-sm p-2 text-sm min-h-[80px]" data-testid="med-notes" />
      </div>
      <button type="submit" disabled={working}
        className="w-full bg-cyan-800 text-white py-3 rounded-sm font-medium hover:bg-cyan-900 disabled:opacity-60"
        data-testid="med-save">
        {working ? <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> : <Save className="h-4 w-4 inline mr-2" />}
        Enregistrer le dossier
      </button>
    </form>
  );
}

// ============ TOUCHE 11 — Formulaire de suggestion d'amélioration ============
function SuggestionForm({ working, setWorking, onDone }) {
  const [category, setCategory] = useState("amelioration");
  const [message, setMessage] = useState("");
  const [contactBack, setContactBack] = useState("");
  const CATEGORIES = [
    { id: "amelioration", label: "✨ Amélioration d'une fonctionnalité existante" },
    { id: "idee", label: "💡 Nouvelle idée / nouvelle tuile" },
    { id: "bug", label: "🐛 Quelque chose ne marche pas" },
    { id: "design", label: "🎨 Design / lisibilité / accessibilité" },
    { id: "traduction", label: "🌍 Erreur de traduction ou de langue" },
    { id: "autre", label: "💬 Autre" },
  ];
  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) { toast.error("Ajoutez quelques mots de plus"); return; }
    setWorking(true);
    try {
      const res = await api.post("/suggestions", {
        message: message.trim(),
        category,
        contact_back: contactBack.trim() || null,
      });
      toast.success(res.data?.message || "Merci pour votre suggestion !");
      setMessage(""); setContactBack("");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setWorking(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="suggestion-form">
      <div className="bg-yellow-700 text-white p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <Lightbulb className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 11 · Votre voix</p>
          <h2 className="text-xl font-serif-display">Suggérer une amélioration</h2>
        </div>
      </div>
      <form onSubmit={submit} className="p-6 space-y-4">
        <p className="text-sm text-slate-700">
          Vos retours nous aident à améliorer "Un Avocat en Or". Donnez-nous votre avis sincère — il sera lu et étudié.
        </p>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-2">Type de retour</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`text-left text-sm rounded-sm border px-3 py-2 transition ${
                  category === c.id
                    ? "bg-yellow-700 text-white border-yellow-700"
                    : "bg-white text-slate-700 border-slate-300 hover:border-yellow-600"
                }`}
                data-testid={`suggestion-cat-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-2">Votre suggestion</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Expliquez votre idée ou ce qui pourrait être amélioré..."
            className="w-full min-h-[140px] border border-slate-300 rounded-sm p-3 text-sm focus:border-yellow-700 focus:outline-none"
            data-testid="suggestion-message"
            required
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slate-700 mb-2">
            (Optionnel) Email ou téléphone pour vous recontacter
          </label>
          <input
            type="text"
            value={contactBack}
            onChange={(e) => setContactBack(e.target.value)}
            placeholder="Laissez vide si vous ne souhaitez pas être recontacté"
            className="w-full border border-slate-300 rounded-sm p-3 text-sm focus:border-yellow-700 focus:outline-none"
            data-testid="suggestion-contact"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => onDone()}
            className="flex-1 border border-slate-300 text-slate-700 rounded-sm py-3 text-sm hover:bg-slate-50"
            data-testid="suggestion-cancel"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={working}
            className="flex-1 bg-yellow-700 text-white rounded-sm py-3 text-sm font-medium hover:bg-yellow-800 disabled:opacity-60"
            data-testid="suggestion-submit"
          >
            {working ? <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> : <Send className="h-4 w-4 inline mr-2" />}
            Envoyer ma suggestion
          </button>
        </div>
      </form>
    </div>
  );
}

// ============ BOUCLIER — Écran de choix entre 2 modes ============
function BouclierChoice({ onLive, onPrevention }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="bouclier-choice">
      <div className="bg-orange-600 text-white p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 3 · Bouclier</p>
          <h2 className="font-serif-display text-2xl font-medium">Comment puis-je vous protéger ?</h2>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-4">
        <p className="text-sm text-slate-600 mb-2">Choisissez le mode adapté à votre situation :</p>

        <button
          type="button"
          onClick={onLive}
          className="w-full border-2 border-orange-600 hover:bg-orange-50 rounded-sm p-5 text-left transition-colors flex items-start gap-4"
          data-testid="bouclier-choice-live"
        >
          <div className="h-12 w-12 rounded-sm bg-orange-600 text-white flex items-center justify-center flex-shrink-0">
            <Swords className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600 font-medium mb-1">Maintenant</p>
            <h3 className="font-serif-display text-xl font-medium text-slate-900 mb-1">C'est en train de se passer</h3>
            <p className="text-sm text-slate-600">Conflit, agression, dispute en cours. Caméra + audio + GPS. Plainte au procureur prête.</p>
          </div>
          <ArrowLeft className="h-5 w-5 text-slate-400 rotate-180 flex-shrink-0 mt-2" />
        </button>

        <button
          type="button"
          onClick={onPrevention}
          className="w-full border-2 border-slate-900 hover:bg-slate-50 rounded-sm p-5 text-left transition-colors flex items-start gap-4"
          data-testid="bouclier-choice-prevention"
        >
          <div className="h-12 w-12 rounded-sm bg-slate-900 text-[#B89C65] flex items-center justify-center flex-shrink-0">
            <Shield className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-700 font-medium mb-1">Préventif</p>
            <h3 className="font-serif-display text-xl font-medium text-slate-900 mb-1">Je pars dans une situation tendue</h3>
            <p className="text-sm text-slate-600">RDV huissier, contrôle, réunion sensible. Audio enregistré pour vous, durée libre. Effacement automatique sous 24h.</p>
          </div>
          <ArrowLeft className="h-5 w-5 text-slate-400 rotate-180 flex-shrink-0 mt-2" />
        </button>

        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-4">
          <p className="text-xs text-amber-800 leading-relaxed">
            ⚖️ <strong>Rappel légal Belgique</strong> : enregistrer une conversation où vous participez est légal pour usage privé.
            Le partager ou l'utiliser comme preuve nécessite le consentement des autres personnes (art. 314bis CP).
          </p>
        </div>
      </div>
    </div>
  );
}

// ============ BOUCLIER PRÉVENTIF — Enregistrement à durée libre + 24h auto-suppression ============
function BouclierPreventionFlow({ working, setWorking, onDone }) {
  const [duration, setDuration] = useState(30); // minutes
  const [context, setContext] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [geo, setGeo] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Capture geo on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    };
  }, []);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mime });
        const f = new File([blob], `bouclier_${Date.now()}.webm`, { type: mime });
        setAudioFile(f);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsedSec(0);
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const sec = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedSec(sec);
        // Auto-stop at duration limit
        if (sec >= duration * 60) {
          stopRecording();
          toast.success(`Durée atteinte (${duration} min) — enregistrement terminé`);
        }
      }, 1000);
    } catch {
      toast.error("Impossible d'accéder au micro — autorisez l'accès");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const submit = async () => {
    if (!audioFile) { toast.error("Lancez d'abord l'enregistrement"); return; }
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", audioFile);
      fd.append("duration_minutes", String(duration));
      if (context.trim()) fd.append("context", context);
      if (geo) fd.append("geo", JSON.stringify(geo));
      fd.append("occurred_at", new Date().toISOString());
      const res = await api.post("/sos/bouclier/prevention", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success("Enregistrement sauvegardé · effacement auto sous 24h");
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setWorking(false); }
  };

  const progress = recording ? Math.min(100, (elapsedSec / (duration * 60)) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="bouclier-prevention-form">
      <div className="bg-slate-900 text-[#B89C65] p-6 flex items-center gap-4">
        <div className="h-12 w-12 rounded-sm bg-[#B89C65]/20 flex items-center justify-center">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Bouclier · Préventif</p>
          <h2 className="font-serif-display text-2xl font-medium text-white">Mode protection</h2>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        {!recording && !audioFile && (
          <>
            <p className="text-sm text-slate-600 mb-6">
              Choisissez la durée souhaitée, démarrez l'enregistrement avant d'entrer dans la situation.
              L'audio est privé, accessible 24h, puis effacé automatiquement. Vous pouvez le marquer
              comme preuve avant l'effacement pour le conserver.
            </p>

            <div className="mb-6">
              <Label className="text-xs uppercase tracking-widest mb-3 block">⏱️ Durée d'enregistrement</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {[5, 15, 30, 45, 60, 90].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDuration(m)}
                    className={`text-sm rounded-sm py-2.5 border transition-colors ${
                      duration === m ? "bg-slate-900 text-white border-slate-900 font-medium" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                    }`}
                    data-testid={`bouclier-duration-${m}`}
                  >
                    {m} min
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={duration}
                  onChange={(e) => setDuration(Math.max(1, Math.min(120, parseInt(e.target.value) || 30)))}
                  className="rounded-sm w-24"
                  data-testid="bouclier-duration-custom"
                />
                <span className="text-sm text-slate-500">minutes (max 120)</span>
              </div>
            </div>

            <div className="mb-6">
              <Label className="text-xs uppercase tracking-widest mb-2 block">Contexte (optionnel)</Label>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={2}
                placeholder="RDV huissier pour saisie, je veux garder une trace de ce qu'il dit..."
                className="rounded-sm"
                data-testid="bouclier-context"
              />
            </div>

            <Button
              onClick={startRecording}
              className="rounded-sm bg-red-600 hover:bg-red-700 h-12 px-8 w-full"
              data-testid="bouclier-start"
            >
              <Mic className="h-4 w-4 mr-2" /> Activer le bouclier · {duration} min
            </Button>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-5">
              <p className="text-xs text-amber-800 leading-relaxed">
                💡 <strong>Astuce</strong> : placez le téléphone dans votre poche ou sac, écran vers l'extérieur.
                Le micro capte clairement à travers le tissu jusqu'à 2 m.
              </p>
            </div>
          </>
        )}

        {recording && (
          <div className="text-center py-6" data-testid="bouclier-recording">
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="h-32 w-32 rounded-full bg-red-600 flex items-center justify-center shadow-2xl">
                <Shield className="h-14 w-14 text-white" />
              </div>
              <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-red-600 mb-1 font-bold">🛡️ Bouclier actif</p>
            <p className="font-serif-display text-4xl font-medium text-slate-900 mb-2" data-testid="bouclier-elapsed">
              {formatTime(elapsedSec)}
            </p>
            <p className="text-sm text-slate-500 mb-6">sur {duration} min · arrêt auto à la fin</p>

            <div className="bg-slate-100 rounded-sm h-2 overflow-hidden mb-6">
              <div
                className="h-full bg-red-600 transition-all duration-1000"
                style={{ width: `${progress}%` }}
              />
            </div>

            <Button
              onClick={stopRecording}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-6"
              data-testid="bouclier-stop"
            >
              <MicOff className="h-4 w-4 mr-2" /> Arrêter maintenant
            </Button>
          </div>
        )}

        {audioFile && !recording && (
          <div data-testid="bouclier-ready">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mb-5 flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-emerald-700 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800">
                  ✓ Bouclier complet — {formatTime(elapsedSec)} enregistré ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
                <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Sera effacé automatiquement sous 24h sauf si vous le conservez
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-5">
              Touchez « Analyser et conserver 24h » pour obtenir un résumé court de ce qui s'est dit (utile
              si vous voulez retrouver un détail), ou recommencez si l'enregistrement n'est pas bon.
            </p>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={submit}
                disabled={working}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-6 flex-1"
                data-testid="bouclier-submit"
              >
                {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Traitement…</> : <><Shield className="h-4 w-4 mr-2" />Analyser et conserver 24h</>}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setAudioFile(null); setElapsedSec(0); }}
                className="rounded-sm h-11 text-red-600"
                data-testid="bouclier-retry"
              >
                Recommencer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ ALADIN LAMP — Triage vocal magique ============

function AladinLamp({ working, setWorking, onRoute, onBack }) {
  const [recording, setRecording] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [result, setResult] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Speak the result when arrived (genie voice — higher pitch + magical chime)
  useEffect(() => {
    if (result && voiceOn) {
      const phrase = `${result.label}. ${result.why_short} Premier pas : ${result.first_step}`;
      speakGenieFR(phrase);
    }
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* ignore */ } };
  }, [result, voiceOn]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mime });
        const f = new File([blob], `lamp_${Date.now()}.webm`, { type: mime });
        setAudioFile(f);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Impossible d'accéder au micro");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const submit = async () => {
    if (!audioFile) { toast.error("Frottez la lampe et parlez d'abord"); return; }
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", audioFile);
      const res = await api.post("/triage/audio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "La lampe n'a pas compris");
    } finally { setWorking(false); }
  };

  const goToAction = () => {
    if (!result?.action) return;
    if (["scan", "police", "bagarre", "bureau", "scam_text", "scam_voice"].includes(result.action)) {
      onRoute(result.action);
    } else if (result.action === "chat") {
      window.location.href = "/dashboard";
    } else if (result.action === "document") {
      window.location.href = "/documents";
    } else if (result.action === "analyse") {
      window.location.href = "/analyses";
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="aladin-lamp-flow">
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white p-6 flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <span className="text-3xl">🪔</span>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">Bouton magique · Bêta</p>
          <h2 className="font-serif-display text-2xl font-medium">Frottez la lampe</h2>
        </div>
      </div>

      <div className="p-8">
        {!result && (
          <>
            <p className="text-sm text-slate-600 mb-6">
              Vous ne savez pas quelle touche utiliser ? Racontez simplement votre situation
              en quelques mots, comme à un ami. La lampe choisit pour vous la bonne fonction
              de l'app et vous y amène.
            </p>

            {recording && (
              <div className="bg-amber-50 border-2 border-amber-500 rounded-sm p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                    <Mic className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-amber-700 uppercase tracking-wider">🪔 La lampe vous écoute…</p>
                    <p className="text-xs text-amber-600 mt-1">Parlez clairement. Quand vous avez fini, touchez "Arrêter".</p>
                  </div>
                  <Button type="button" onClick={stopRecording} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm h-9" data-testid="aladin-stop">
                    <MicOff className="h-3.5 w-3.5 mr-1.5" /> Arrêter
                  </Button>
                </div>
              </div>
            )}

            {audioFile && !recording && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-6 flex items-center justify-between">
                <span className="text-sm text-emerald-800">✓ Enregistrement prêt — touchez « Demander à la lampe »</span>
                <Button type="button" variant="ghost" onClick={() => setAudioFile(null)} className="text-red-600 text-xs h-8 rounded-sm">
                  Recommencer
                </Button>
              </div>
            )}

            {!recording && !audioFile && (
              <div className="flex flex-col items-center py-8">
                <button
                  type="button"
                  onClick={startRecording}
                  className="group relative h-32 w-32 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-yellow-600 text-white shadow-2xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
                  data-testid="aladin-start"
                >
                  <span className="text-5xl group-hover:animate-pulse">🪔</span>
                  <span className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping" />
                </button>
                <p className="text-sm font-medium text-slate-900 mt-6">Touchez la lampe pour parler</p>
                <p className="text-xs text-slate-500 mt-1">L'IA décide elle-même quelle touche est la bonne</p>
              </div>
            )}

            {audioFile && (
              <Button
                onClick={submit}
                disabled={working}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8 w-full"
                data-testid="aladin-submit"
              >
                {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />La lampe réfléchit…</> : <>🪔 Demander à la lampe</>}
              </Button>
            )}
          </>
        )}

        {result && (
          <div data-testid="aladin-result">
            <div className="flex items-center justify-end mb-3">
              <button
                type="button"
                onClick={() => {
                  setVoiceOn((v) => {
                    if (v) { try { window.speechSynthesis?.cancel(); } catch {} }
                    return !v;
                  });
                }}
                className="text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline flex items-center gap-1.5"
                data-testid="aladin-voice-toggle"
              >
                {voiceOn ? <>🔊 Voix activée — couper</> : <>🔇 Voix coupée — activer</>}
              </button>
            </div>
            <div className="bg-amber-50 border-2 border-amber-500 rounded-sm p-5 mb-5">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">🪔 La lampe a parlé</p>
              <h3 className="font-serif-display text-2xl font-medium text-slate-900 mb-2" data-testid="aladin-label">
                {result.label}
              </h3>
              <p className="text-sm text-slate-700 mb-3">{result.why_short}</p>
              <div className="border-t border-amber-200 pt-3 mt-3">
                <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">🎯 Premier pas</p>
                <p className="text-sm font-medium text-slate-900">{result.first_step}</p>
              </div>
              {result.urgent && (
                <div className="mt-3 inline-flex items-center gap-2 bg-red-100 text-red-700 text-xs px-3 py-1.5 rounded-sm">
                  <AlertTriangle className="h-3.5 w-3.5" /> URGENT
                </div>
              )}
            </div>

            {result.transcription && (
              <details className="mb-5">
                <summary className="text-xs uppercase tracking-widest text-slate-500 cursor-pointer">Ce que la lampe a entendu</summary>
                <p className="text-sm text-slate-700 mt-2 italic bg-slate-50 p-3 rounded-sm">« {result.transcription} »</p>
              </details>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={goToAction} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 flex-1" data-testid="aladin-go">
                Aller à « {result.label} » <ArrowLeft className="h-3.5 w-3.5 ml-2 rotate-180" />
              </Button>
              <Button onClick={() => { setResult(null); setAudioFile(null); }} variant="outline" className="rounded-sm h-11" data-testid="aladin-retry">
                Recommencer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ VIEW ALERT ============
// ============================================================================
// Touche 10 — INTELLIGENCE COLLABORATIVE des bureaux d'immigration.
// Alerte bienveillante si 5+ utilisateurs à la même ville ont signalé une erreur.
// ============================================================================
function CommunityOfficeHint({ city }) {
  const [hints, setHints] = useState([]);
  useEffect(() => {
    if (!city) return;
    api.get(`/immigration/office_hints?city=${encodeURIComponent(city)}`)
      .then((r) => setHints(r.data?.hints || []))
      .catch(() => {});
  }, [city]);
  if (!hints.length) return null;
  return (
    <div className="bg-amber-50 border-2 border-amber-500 rounded-sm p-4 space-y-3" data-testid="community-office-hints">
      <div className="flex items-center gap-2">
        <span className="text-2xl">🧠</span>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold">Info communauté · Intelligence collective</p>
          <p className="text-xs text-amber-900">Basée sur les retours de nos utilisateurs à {city}</p>
        </div>
      </div>
      {hints.map((h, i) => (
        <div key={i} className="bg-white border border-amber-300 rounded-sm p-3" data-testid={`community-hint-${i}`}>
          <p className="text-sm text-slate-800 leading-relaxed">{h.message}</p>
          {h.demarche_examples?.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-1">Contexte : {h.demarche_examples.join(" · ")}</p>
          )}
          <p className="text-[10px] text-amber-700 mt-1">✅ Confirmé par {h.confirmations} utilisateur·es</p>
        </div>
      ))}
    </div>
  );
}

function OfficeErrorFeedback({ defaultCity = "", defaultDemarche = "" }) {
  const [open, setOpen] = useState(false);
  const [city, setCity] = useState(defaultCity);
  const [wrong, setWrong] = useState("");
  const [better, setBetter] = useState("");
  const [addr, setAddr] = useState("");
  const [reason, setReason] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!city.trim() || !wrong.trim() || !better.trim()) return;
    setLoading(true);
    try {
      await api.post("/immigration/office_feedback", {
        user_city: city.trim(),
        wrong_office: wrong.trim(),
        better_office: better.trim(),
        better_office_address: addr.trim() || null,
        reason: reason.trim() || null,
        demarche: defaultDemarche || null,
      });
      setSent(true);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-sm text-emerald-900" data-testid="office-feedback-thanks">
        ✅ Merci ! Votre signalement est enregistré. Dès que 5 personnes confirment le même bureau, une alerte s'affiche automatiquement pour éviter aux prochains de perdre leur journée.
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 pt-3 mt-2" data-testid="office-feedback-block">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-slate-600 hover:text-slate-900 underline"
          data-testid="open-office-feedback"
        >
          ⚠️ L'agent m'a envoyé au mauvais bureau — signaler
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-2" data-testid="office-feedback-form">
          <p className="text-xs text-slate-600">Aidez la communauté : signalez le mauvais bureau + le bon.</p>
          <input
            className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
            placeholder="Votre ville (ex: Bruxelles)"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            data-testid="feedback-city-input"
          />
          <input
            className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
            placeholder="Bureau où l'agent voulait m'envoyer (ex: Office des Étrangers Liège)"
            value={wrong}
            onChange={(e) => setWrong(e.target.value)}
            required
            data-testid="feedback-wrong-input"
          />
          <input
            className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
            placeholder="Bureau réellement compétent (ex: Antenne Immigration Ixelles)"
            value={better}
            onChange={(e) => setBetter(e.target.value)}
            required
            data-testid="feedback-better-input"
          />
          <input
            className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
            placeholder="Adresse du bon bureau (optionnel)"
            value={addr}
            onChange={(e) => setAddr(e.target.value)}
            data-testid="feedback-addr-input"
          />
          <textarea
            className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
            placeholder="Précision (optionnel) : ce que l'agent vous a dit, pourquoi c'était faux…"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="feedback-reason-input"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded-sm text-slate-600 hover:bg-slate-100" data-testid="feedback-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="text-xs px-3 py-1.5 rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-medium" data-testid="feedback-submit">
              {loading ? "Envoi…" : "Envoyer à la communauté"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


function AlertView({ alert, onReload, onBack }) {
  const parsed = alert.parsed || {};
  const isScan = alert.scenario === "scanner" || alert.trigger === "scan";
  const isScenario = alert.trigger === "scenario";
  const isScamText = alert.scenario === "scam_text";
  const isScamVoice = alert.scenario === "scam_voice";
  const isScam = isScamText || isScamVoice;
  const isBouclier = alert.trigger === "bouclier";
  const [keeping, setKeeping] = useState(false);
  const [kept, setKept] = useState(alert.kept_as_proof === true);

  const handleDelete = async () => {
    try {
      await api.delete(`/sos/alerts/${alert.alert_id}`);
      toast.success("Supprimée");
      onReload();
      onBack();
    } catch { toast.error("Erreur"); }
  };

  const mailBody = parsed.lettre_réponse || parsed.mail_comité_P || parsed.mail_comite_P || parsed.plainte_police || parsed.mail_plainte || "";
  const recipient = isScan ? parsed.qui_envoie || ""
    : alert.scenario === "police" ? "comite.p@comitep.be"
    : alert.scenario === "bagarre" ? "procureur@just.fgov.be"
    : "";
  const subject = isScan ? `Réponse : ${parsed.type_document || "courrier"}` : `Plainte — ${alert.label}`;
  const mailtoHref = mailBody ? `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailBody)}` : null;

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); }
    catch { toast.error("Copie impossible"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="alert-view">
      <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65]">{alert.scenario || alert.trigger}</p>
          <h2 className="font-serif-display text-2xl font-medium">{parsed.type_document || parsed.situation || alert.label}</h2>
          <p className="text-xs text-slate-500 mt-1">{new Date(alert.created_at).toLocaleString("fr-BE")}</p>
        </div>
        <Button onClick={handleDelete} variant="outline" className="rounded-sm text-red-600" data-testid="alert-delete">
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer
        </Button>
      </div>

      {parsed.raw && (
        <div className="p-8">
          <pre className="whitespace-pre-wrap text-sm text-slate-800">{parsed.raw}</pre>
        </div>
      )}

      {isScan && !parsed.raw && (
        <div className="p-8 space-y-6">
          <Section title="📄 Ce document est…" testid="scan-type">
            <p className="text-sm text-slate-700"><strong>{parsed.type_document}</strong> — envoyé par {parsed.qui_envoie}</p>
          </Section>
          <Section title="🗣️ Ce qu'ils veulent (en langue simple)" testid="scan-plain">
            <p className="text-sm text-slate-700">{parsed.ce_quils_veulent}</p>
          </Section>
          <BulletList title="⏰ Délais importants" items={parsed.délais_importants} testid="scan-deadlines" />
          <Section title="⚠️ Si vous ne faites rien" testid="scan-consequences">
            <p className="text-sm text-slate-700">{parsed.conséquences_si_rien_faire}</p>
          </Section>
          <BulletList title="⚖️ Vos droits" items={parsed.vos_droits} testid="scan-rights" />
          <MailActionBlock title="✉️ Lettre de réponse prête" body={parsed.lettre_réponse} mailtoHref={mailtoHref} onCopy={copyToClipboard} testid="scan-letter" />
        </div>
      )}

      {isScenario && !parsed.raw && (
        <div className="p-8 space-y-6">
          <Section title="📋 Situation" testid="scen-situation"><p className="text-sm text-slate-700">{parsed.situation}</p></Section>

          {/* SANS-PAPIERS: traduction immédiate en langue d'origine */}
          {alert.scenario === "sans_papiers" && parsed.langue_detectee && parsed.langue_detectee !== "fr" && parsed.traduction_resume && (
            <div className="bg-blue-50 border-2 border-blue-700 rounded-sm p-5" data-testid="sans-papiers-translation">
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-700 mb-2 font-bold">
                🌍 Langue détectée : {parsed.langue_detectee.toUpperCase()} — Résumé dans votre langue
              </p>
              <p className="text-base text-slate-900 whitespace-pre-wrap" dir={["ar", "fa", "ps", "ur"].includes(parsed.langue_detectee) ? "rtl" : "ltr"}>
                {parsed.traduction_resume}
              </p>
            </div>
          )}

          {alert.scenario === "harassment" && (parsed.type_harcelement || parsed.gravite || parsed.victime_profil) && (
            <div className="grid grid-cols-3 gap-3" data-testid="harassment-badges">
              {parsed.type_harcelement && (
                <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-rose-700">Type</p>
                  <p className="text-sm font-medium text-rose-900 mt-1">{parsed.type_harcelement}</p>
                </div>
              )}
              {parsed.gravite && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-amber-700">Gravité</p>
                  <p className="text-sm font-medium text-amber-900 mt-1">{parsed.gravite}</p>
                </div>
              )}
              {parsed.victime_profil && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-slate-600">Profil</p>
                  <p className="text-sm font-medium text-slate-900 mt-1">{parsed.victime_profil}</p>
                </div>
              )}
            </div>
          )}

          {alert.scenario === "animal_abuse" && (parsed.type_maltraitance || parsed.urgence || parsed.animal) && (
            <div className="grid grid-cols-3 gap-3" data-testid="animal-badges">
              {parsed.type_maltraitance && (
                <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-amber-700">Type</p>
                  <p className="text-sm font-medium text-amber-900 mt-1">{parsed.type_maltraitance}</p>
                </div>
              )}
              {parsed.urgence && (
                <div className="bg-red-50 border border-red-300 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-red-700">Urgence</p>
                  <p className="text-sm font-medium text-red-900 mt-1">{parsed.urgence}</p>
                </div>
              )}
              {parsed.animal && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-700">Animal</p>
                  <p className="text-sm font-medium text-emerald-900 mt-1">{parsed.animal}</p>
                </div>
              )}
            </div>
          )}

          <BulletList title="🚨 Abus / risques potentiels" items={parsed.abus_potentiels || parsed.risques_juridiques_pour_vous || parsed.infractions_legales} testid="scen-risks" />
          <BulletList title="🛡️ Droits fondamentaux" items={parsed.droits_fondamentaux} testid="sans-papiers-rights" />
          <BulletList title="⚖️ Lois applicables" items={parsed.lois_citées} testid="scen-laws" />
          <BulletList title="💬 À dire MAINTENANT" items={parsed.phrases_à_dire} testid="scen-say" highlight />
          {alert.scenario === "sans_papiers" && Array.isArray(parsed.phrases_traduites) && parsed.phrases_traduites.length > 0 && (
            <BulletList title={`💬 ${(parsed.langue_detectee || "").toUpperCase()} — Mêmes phrases dans votre langue`} items={parsed.phrases_traduites} testid="sans-papiers-phrases-translated" highlight />
          )}

          {alert.scenario === "sans_papiers" && parsed.parcours_papiers && (
            <>
              <CommunityOfficeHint city={parsed.parcours_papiers.ville || alert?.geo?.city || alert?.geo?.address?.split(",")[0]?.trim()} />
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-700 rounded-sm p-5 space-y-4" data-testid="sans-papiers-parcours">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-sm bg-blue-700 flex items-center justify-center text-white text-2xl flex-shrink-0">🗺️</div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold">Parcours guidé pour vos papiers</p>
                  <h3 className="font-serif-display text-lg font-medium text-slate-900">{parsed.parcours_papiers.bureau_recommande}</h3>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {parsed.parcours_papiers.adresse && (
                  <div className="bg-white border border-blue-200 rounded-sm p-3" data-testid="parcours-adresse">
                    <p className="text-xs font-bold text-blue-700 mb-1">📍 ADRESSE</p>
                    <p className="text-slate-800">{parsed.parcours_papiers.adresse}{parsed.parcours_papiers.ville ? `, ${parsed.parcours_papiers.ville}` : ""}</p>
                  </div>
                )}
                {parsed.parcours_papiers.horaires && (
                  <div className="bg-white border border-blue-200 rounded-sm p-3" data-testid="parcours-horaires">
                    <p className="text-xs font-bold text-blue-700 mb-1">🕒 HORAIRES</p>
                    <p className="text-slate-800">{parsed.parcours_papiers.horaires}</p>
                  </div>
                )}
                {parsed.parcours_papiers.telephone && (
                  <div className="bg-white border border-blue-200 rounded-sm p-3" data-testid="parcours-tel">
                    <p className="text-xs font-bold text-blue-700 mb-1">📞 TÉLÉPHONE</p>
                    <a href={`tel:${parsed.parcours_papiers.telephone.replace(/\s/g, "")}`} className="text-blue-700 underline">{parsed.parcours_papiers.telephone}</a>
                  </div>
                )}
                {parsed.parcours_papiers.site_web && (
                  <div className="bg-white border border-blue-200 rounded-sm p-3" data-testid="parcours-site">
                    <p className="text-xs font-bold text-blue-700 mb-1">🌐 SITE OFFICIEL</p>
                    <a href={parsed.parcours_papiers.site_web} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline break-all">{parsed.parcours_papiers.site_web}</a>
                  </div>
                )}
              </div>

              {parsed.parcours_papiers.gps_url && (
                <a
                  href={parsed.parcours_papiers.gps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-700 hover:bg-blue-800 text-white text-center font-bold py-4 rounded-sm transition-colors"
                  data-testid="parcours-gps-btn"
                >
                  🗺️ OUVRIR L'ITINÉRAIRE GPS →
                </a>
              )}

              {parsed.parcours_papiers.transport_public && (
                <div className="bg-amber-50 border border-amber-300 rounded-sm p-3" data-testid="parcours-transport">
                  <p className="text-xs font-bold text-amber-800 mb-1">🚇 TRANSPORT PUBLIC</p>
                  <p className="text-sm text-slate-800">{parsed.parcours_papiers.transport_public}</p>
                </div>
              )}

              {Array.isArray(parsed.parcours_papiers.documents_a_apporter) && parsed.parcours_papiers.documents_a_apporter.length > 0 && (
                <BulletList title="📋 Documents à apporter" items={parsed.parcours_papiers.documents_a_apporter} testid="parcours-docs" />
              )}
              {Array.isArray(parsed.parcours_papiers.preparer_avant_rdv) && parsed.parcours_papiers.preparer_avant_rdv.length > 0 && (
                <BulletList title="✅ À préparer AVANT le rendez-vous" items={parsed.parcours_papiers.preparer_avant_rdv} testid="parcours-prep" />
              )}
              {Array.isArray(parsed.parcours_papiers.conseils_jour_j) && parsed.parcours_papiers.conseils_jour_j.length > 0 && (
                <BulletList title="🎯 Conseils du jour J" items={parsed.parcours_papiers.conseils_jour_j} testid="parcours-tips" highlight />
              )}
              <OfficeErrorFeedback
                defaultCity={parsed.parcours_papiers.ville || alert?.geo?.city || ""}
                defaultDemarche={parsed.type_situation || ""}
              />
            </div>
            </>
          )}
          <BulletList title="🚫 À NE PAS faire" items={parsed.à_ne_pas_faire || parsed.à_ne_pas_faire_liste} testid="scen-dont" />
          <BulletList title="📋 Actions immédiates" items={parsed.actions_immédiates || parsed.actions_immediates} testid="scen-actions" />
          <BulletList title="📎 Documents à exiger" items={parsed.documents_à_exiger || parsed.documents_à_demander} testid="scen-docs" />

          {(alert.scenario === "harassment" || alert.scenario === "animal_abuse") && parsed.preuves_à_recolter && (
            <BulletList title="📸 Preuves à récolter" items={parsed.preuves_à_recolter} testid="scen-evidence" />
          )}

          {Array.isArray(parsed.contacts_utiles) && parsed.contacts_utiles.length > 0 && (
            <Section title="📞 Qui contacter — vérifié pour votre pays" testid="scen-contacts">
              <div className="space-y-3">
                {parsed.contacts_utiles.map((c, idx) => (
                  <div key={idx} className="border-l-4 border-slate-700 bg-slate-50/50 p-3 rounded-r-sm" data-testid={`contact-${idx}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-600">{c.type || "Contact"}</p>
                        <p className="font-medium text-sm text-slate-900">{c.nom}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {c.url && (
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline hover:text-blue-900" data-testid={`contact-${idx}-url`}>
                            Site / S'inscrire
                          </a>
                        )}
                        {c.tel && (
                          <a href={`tel:${String(c.tel).replace(/[^\d+]/g, '')}`} className="text-xs text-blue-700 underline hover:text-blue-900" data-testid={`contact-${idx}-tel`}>
                            📞 {c.tel}
                          </a>
                        )}
                      </div>
                    </div>
                    {c.role && <p className="text-xs text-slate-600">{c.role}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {alert.scenario === "harassment" && parsed.verification_promesses && (
            <Section title="✅ Vérifier qu'ils tiennent parole" testid="harassment-verify">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{parsed.verification_promesses}</p>
            </Section>
          )}

          {/* Mails spécifiques par scénario */}
          {alert.scenario === "harassment" && parsed.mail_harceleur && (
            <MailActionBlock title="✉️ Courrier au harceleur (trace écrite ferme)" body={parsed.mail_harceleur}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Cessation immédiate des agissements")}&body=${encodeURIComponent(parsed.mail_harceleur)}`}
              onCopy={copyToClipboard} testid="harassment-mail-harceleur" />
          )}
          {alert.scenario === "harassment" && parsed.mail_hierarchie && (
            <MailActionBlock title="✉️ Signalement à la hiérarchie / DRH / direction" body={parsed.mail_hierarchie}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Signalement officiel de harcèlement")}&body=${encodeURIComponent(parsed.mail_hierarchie)}`}
              onCopy={copyToClipboard} testid="harassment-mail-hierarchie" />
          )}
          {alert.scenario === "harassment" && parsed.mail_inspection && (
            <MailActionBlock title="✉️ Plainte à l'inspection du travail" body={parsed.mail_inspection}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Plainte officielle pour harcèlement")}&body=${encodeURIComponent(parsed.mail_inspection)}`}
              onCopy={copyToClipboard} testid="harassment-mail-inspection" />
          )}

          {alert.scenario === "harassment" && parsed.signalement_plateforme && (
            <MailActionBlock title="📱 Signalement plateforme (Instagram / TikTok / etc.)" body={parsed.signalement_plateforme}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Signalement de cyber-harcèlement")}&body=${encodeURIComponent(parsed.signalement_plateforme)}`}
              onCopy={copyToClipboard} testid="harassment-signalement-plateforme" />
          )}

          {alert.scenario === "harassment" && parsed.requisition_procureur && (
            <MailActionBlock title="⚖️ Demande de réquisition au procureur (harceleur anonyme)" body={parsed.requisition_procureur}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Demande de réquisition judiciaire — identification cyber-harceleur")}&body=${encodeURIComponent(parsed.requisition_procureur)}`}
              onCopy={copyToClipboard} testid="harassment-requisition" />
          )}

          {alert.scenario === "harassment" && parsed.mail_parents_harceleur && (
            <MailActionBlock title="✉️ Lettre aux parents du harceleur (si mineur — responsabilité civile)" body={parsed.mail_parents_harceleur}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Mise en demeure — agissements de votre enfant")}&body=${encodeURIComponent(parsed.mail_parents_harceleur)}`}
              onCopy={copyToClipboard} testid="harassment-parents-letter" />
          )}

          {alert.scenario === "harassment" && parsed.signalement_cpms_psychologue_scolaire && (
            <MailActionBlock title="🧠 Demande accompagnement psy scolaire (CPMS / Pôle médico-social)" body={parsed.signalement_cpms_psychologue_scolaire}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Demande d'accompagnement — harcèlement scolaire")}&body=${encodeURIComponent(parsed.signalement_cpms_psychologue_scolaire)}`}
              onCopy={copyToClipboard} testid="harassment-cpms" />
          )}

          {alert.scenario === "harassment" && parsed.saisine_delegue_droits_enfant && (
            <MailActionBlock title="🛡️ Saisine du Délégué aux droits de l'enfant" body={parsed.saisine_delegue_droits_enfant}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Saisine — atteinte aux droits de l'enfant")}&body=${encodeURIComponent(parsed.saisine_delegue_droits_enfant)}`}
              onCopy={copyToClipboard} testid="harassment-defenseur-enfant" />
          )}

          {alert.scenario === "harassment" && parsed.recours_si_ecole_inactive && (
            <MailActionBlock title="📢 Recours si école/employeur ne réagit pas dans 7 jours" body={parsed.recours_si_ecole_inactive}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Escalade — absence de mesure depuis signalement")}&body=${encodeURIComponent(parsed.recours_si_ecole_inactive)}`}
              onCopy={copyToClipboard} testid="harassment-recours-ecole" />
          )}

          {alert.scenario === "harassment" && parsed.plan_action_30_jours && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-700 rounded-sm p-5" data-testid="harassment-plan-30j">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-amber-700 flex items-center justify-center text-white text-xl flex-shrink-0">📅</div>
                <h3 className="font-serif-display text-lg font-medium text-slate-900">Plan d'action sur 30 jours</h3>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{parsed.plan_action_30_jours}</p>
            </div>
          )}

          {alert.scenario === "harassment" && parsed.safety_plan && (
            <div className="bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-700 rounded-sm p-5" data-testid="harassment-safety-plan">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-red-700 flex items-center justify-center text-white text-xl flex-shrink-0">🛟</div>
                <h3 className="font-serif-display text-lg font-medium text-slate-900">Plan de sécurité personnel</h3>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{parsed.safety_plan}</p>
            </div>
          )}

          {alert.scenario === "harassment" && Array.isArray(parsed.contacts_aide_psychologique) && parsed.contacts_aide_psychologique.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-700 rounded-sm p-5" data-testid="harassment-psy-contacts">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-blue-700 flex items-center justify-center text-white text-xl flex-shrink-0">💙</div>
                <h3 className="font-serif-display text-lg font-medium text-slate-900">Soutien psychologique anonyme et gratuit</h3>
              </div>
              <div className="space-y-2">
                {parsed.contacts_aide_psychologique.map((c, i) => (
                  <div key={i} className="bg-white border border-blue-200 rounded-sm p-3 text-sm">
                    <p className="font-bold text-blue-900">{c.nom}</p>
                    {c.tel && <a href={`tel:${c.tel.replace(/\s/g, "")}`} className="text-blue-700 underline font-semibold">📞 {c.tel}</a>}
                    {c.horaires && <p className="text-xs text-slate-600 mt-1">🕐 {c.horaires}</p>}
                    {c.specialite && <p className="text-xs text-slate-600">{c.specialite}</p>}
                    {c.anonymat && <p className="text-xs text-blue-700 font-semibold mt-1">✓ Anonyme</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {alert.scenario === "animal_abuse" && parsed.plainte_police && (
            <MailActionBlock title="✉️ Plainte police (infraction pénale)" body={parsed.plainte_police}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Plainte — maltraitance animale")}&body=${encodeURIComponent(parsed.plainte_police)}`}
              onCopy={copyToClipboard} testid="animal-plainte" />
          )}
          {alert.scenario === "animal_abuse" && parsed.signalement_service_animal && (
            <MailActionBlock title="✉️ Signalement service vétérinaire officiel" body={parsed.signalement_service_animal}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Signalement de maltraitance animale")}&body=${encodeURIComponent(parsed.signalement_service_animal)}`}
              onCopy={copyToClipboard} testid="animal-signalement" />
          )}
          {alert.scenario === "animal_abuse" && parsed.mail_proprietaire && (
            <MailActionBlock title="✉️ Mise en demeure au propriétaire" body={parsed.mail_proprietaire}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Mise en demeure — cessation de la maltraitance")}&body=${encodeURIComponent(parsed.mail_proprietaire)}`}
              onCopy={copyToClipboard} testid="animal-mail-proprietaire" />
          )}

          {alert.scenario === "sans_papiers" && parsed.courrier_administration && (
            <MailActionBlock title="✉️ Courrier à l'administration" body={parsed.courrier_administration}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Demande de motivation / recours")}&body=${encodeURIComponent(parsed.courrier_administration)}`}
              onCopy={copyToClipboard} testid="sans-papiers-mail-admin" />
          )}
          {alert.scenario === "sans_papiers" && parsed.courrier_defenseur_droits && (
            <MailActionBlock title="✉️ Saisine du Défenseur des droits / médiateur" body={parsed.courrier_defenseur_droits}
              mailtoHref={`mailto:?subject=${encodeURIComponent("Saisine pour abus de fonctionnaire")}&body=${encodeURIComponent(parsed.courrier_defenseur_droits)}`}
              onCopy={copyToClipboard} testid="sans-papiers-mail-defenseur" />
          )}

          {!["harassment", "animal_abuse", "sans_papiers"].includes(alert.scenario) && (
            <MailActionBlock
              title="✉️ Courrier prêt à envoyer"
              body={parsed.mail_comité_P || parsed.mail_comite_P || parsed.plainte_police || parsed.mail_plainte}
              mailtoHref={mailtoHref}
              onCopy={copyToClipboard}
              testid="scen-mail"
            />
          )}

          <CollectiveCTA
            alertId={alert.alert_id}
            scenario={alert.scenario}
            commune={alert.geo?.commune || alert.geo?.city || null}
            pays={alert.country || null}
          />
        </div>
      )}

      {isScam && !parsed.raw && (
        <ScamVerdictView alert={alert} parsed={parsed} isVoice={isScamVoice} />
      )}

      {isBouclier && (
        <div className="p-8 space-y-6" data-testid="bouclier-view">
          {/* Bandeau expiration */}
          {!kept && alert.expires_at && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-sm p-4 flex items-start gap-3" data-testid="bouclier-expiry-banner">
              <Clock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">⏰ Effacement automatique programmé</p>
                <p className="text-xs text-amber-800 mt-1">
                  Cet enregistrement sera supprimé le <strong>{new Date(alert.expires_at).toLocaleString("fr-BE")}</strong> (dans ~24h).
                  Si vous souhaitez le conserver comme preuve, touchez « Garder ».
                </p>
                <Button
                  onClick={async () => {
                    setKeeping(true);
                    try {
                      await api.post(`/sos/alerts/${alert.alert_id}/keep`);
                      toast.success("Enregistrement conservé comme preuve");
                      setKept(true);
                      onReload();
                    } catch {
                      toast.error("Erreur");
                    } finally { setKeeping(false); }
                  }}
                  disabled={keeping}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9 text-sm mt-3"
                  data-testid="bouclier-keep-button"
                >
                  {keeping ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
                  Garder comme preuve
                </Button>
              </div>
            </div>
          )}
          {kept && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-center gap-2" data-testid="bouclier-kept-banner">
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
              <p className="text-sm text-emerald-800">✓ Conservé comme preuve — ne sera plus effacé automatiquement</p>
            </div>
          )}

          {parsed.raw ? (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">📋 Note</p>
              <p className="text-sm text-slate-700">{parsed.raw}</p>
            </div>
          ) : (
            <>
              {parsed.résumé_court && (
                <Section title="📋 Résumé" testid="bouclier-summary">
                  <p className="text-sm text-slate-700 leading-relaxed">{parsed.résumé_court}</p>
                </Section>
              )}
              <BulletList title="🔑 Points importants à retenir" items={parsed.points_importants} testid="bouclier-points" highlight />
              <BulletList title="🚨 Abus / pressions détectés" items={parsed.abus_potentiels} testid="bouclier-abuses" />
              <BulletList title="💾 Détails pour plus tard" items={parsed.à_retenir_pour_plus_tard} testid="bouclier-later" />
              {parsed.recommandation && (
                <Section title="🎯 Recommandation" testid="bouclier-reco">
                  <p className="text-sm text-slate-700">{parsed.recommandation}</p>
                </Section>
              )}
            </>
          )}
        </div>
      )}

      {alert.transcription && (
        <details className="p-6 border-t border-slate-200 bg-slate-50">
          <summary className="text-xs uppercase tracking-widest text-slate-500 cursor-pointer">Transcription audio</summary>
          <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap" data-testid="alert-transcription">{alert.transcription}</p>
        </details>
      )}

      {(alert.geo || alert.occurred_at || alert.file_path) && (
        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">🔬 Éléments de preuve</p>
          <div className="space-y-2 text-sm text-slate-700">
            {alert.occurred_at && (
              <p data-testid="evidence-time">⏰ <strong>Horodatage</strong> : {new Date(alert.occurred_at).toLocaleString("fr-BE")}</p>
            )}
            {alert.geo?.address && (
              <p data-testid="evidence-location">📍 <strong>Lieu</strong> : {alert.geo.address}</p>
            )}
            {alert.geo?.lat && (
              <p className="text-xs text-slate-500">
                Coordonnées : {alert.geo.lat.toFixed(6)}, {alert.geo.lon.toFixed(6)}
                {alert.geo.accuracy ? ` (±${Math.round(alert.geo.accuracy)}m)` : ""}
              </p>
            )}
            {alert.file_path && (
              <a
                href={`${API}/sos/alerts/${alert.alert_id}/file`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm px-3 py-2"
                data-testid="evidence-download"
              >
                <FileText className="h-3.5 w-3.5" /> Télécharger l'enregistrement (preuve)
              </a>
            )}
          </div>
        </div>
      )}

      <div className="p-6 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2">
        <a href="tel:112" className="inline-flex items-center gap-2 bg-red-600 text-white text-sm rounded-sm px-3 py-2 hover:bg-red-700" data-testid="call-112">
          <Phone className="h-3.5 w-3.5" /> 112 Urgence
        </a>
        <a href="tel:101" className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm rounded-sm px-3 py-2 hover:bg-slate-800" data-testid="call-101">
          <Phone className="h-3.5 w-3.5" /> 101 Police
        </a>
        <a href="tel:0032025198330" className="inline-flex items-center gap-2 bg-slate-700 text-white text-sm rounded-sm px-3 py-2 hover:bg-slate-800" data-testid="call-bar">
          <Phone className="h-3.5 w-3.5" /> Avocats BXL
        </a>
      </div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{title}</p>
      {children}
    </div>
  );
}

function BulletList({ title, items, testid, highlight }) {
  if (!items || !Array.isArray(items) || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">{title}</p>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className={`text-sm flex gap-3 ${highlight ? "bg-amber-50 border-l-2 border-[#B89C65] p-3 rounded-sm" : ""}`}>
            <span className="text-[#B89C65] flex-shrink-0">—</span>
            <span className="text-slate-700">{typeof it === "string" ? it : JSON.stringify(it)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MailActionBlock({ title, body, mailtoHref, onCopy, testid }) {
  if (!body) return null;
  return (
    <div className="border-2 border-[#B89C65] rounded-sm overflow-hidden" data-testid={testid}>
      <div className="bg-[#B89C65]/10 px-4 py-3 border-b border-[#B89C65]">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] font-medium">{title}</p>
      </div>
      <div className="p-4 bg-white">
        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed mb-4" data-testid={`${testid}-body`}>
          {body}
        </pre>
        <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
          {mailtoHref && (
            <a
              href={mailtoHref}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-sm px-6 py-3 text-sm font-medium"
              data-testid={`${testid}-send-button`}
            >
              <Send className="h-4 w-4" /> ENVOYER (ouvre votre mail)
            </a>
          )}
          <button
            type="button"
            onClick={() => onCopy(body)}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-4 py-3 text-sm"
            data-testid={`${testid}-copy-button`}
          >
            <Copy className="h-4 w-4" /> Copier
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ ANTI-ARNAQUE — Verdict UI ============
const VERDICT_STYLES = {
  legitime: { label: "Légitime", icon: ShieldCheck, color: "bg-emerald-600", ring: "border-emerald-600", text: "text-emerald-700", bg: "bg-emerald-50" },
  appel_legitime: { label: "Appel légitime", icon: ShieldCheck, color: "bg-emerald-600", ring: "border-emerald-600", text: "text-emerald-700", bg: "bg-emerald-50" },
  suspect: { label: "Suspect", icon: AlertTriangle, color: "bg-amber-500", ring: "border-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
  voix_synthetique_suspecte: { label: "Voix synthétique suspecte", icon: AlertTriangle, color: "bg-amber-500", ring: "border-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
  arnaque_probable: { label: "Arnaque probable", icon: ShieldX, color: "bg-orange-600", ring: "border-orange-600", text: "text-orange-700", bg: "bg-orange-50" },
  arnaque: { label: "Arnaque", icon: ShieldX, color: "bg-red-600", ring: "border-red-600", text: "text-red-700", bg: "bg-red-50" },
  arnaque_certaine: { label: "Arnaque certaine", icon: ShieldX, color: "bg-red-600", ring: "border-red-600", text: "text-red-700", bg: "bg-red-50" },
};

function ScamVerdictView({ alert, parsed, isVoice }) {
  const [open, setOpen] = useState(false);
  const verdict = (parsed.verdict || "suspect").toLowerCase();
  const meta = VERDICT_STYLES[verdict] || VERDICT_STYLES.suspect;
  const Icon = meta.icon;
  const score = typeof parsed.risk_score === "number" ? parsed.risk_score : null;
  const typeLabel = parsed.type_arnaque_probable;
  const action = parsed.à_faire_maintenant || parsed.a_faire_maintenant;
  const signalement = parsed.signalement_officiel;
  const flags = isVoice ? parsed.indices_arnaque : parsed.red_flags;
  const verifications = isVoice ? parsed.questions_de_verification : parsed.verifications_à_faire;
  const dontList = parsed.à_ne_pas_faire || parsed.a_ne_pas_faire;

  return (
    <div className="p-8 space-y-6" data-testid="scam-verdict-view">
      {/* VERDICT BANNER */}
      <div className={`${meta.bg} border-2 ${meta.ring} rounded-sm overflow-hidden`} data-testid="scam-verdict-banner">
        <div className={`${meta.color} text-white px-5 py-4 flex items-center gap-4`}>
          <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] opacity-80">Verdict</p>
            <h3 className="font-serif-display text-2xl font-medium leading-tight" data-testid="scam-verdict-label">
              {meta.label}
            </h3>
            {typeLabel && (
              <p className="text-sm opacity-90 mt-0.5">{typeLabel}</p>
            )}
          </div>
          {score !== null && (
            <div className="text-right flex-shrink-0">
              <p className="text-xs uppercase tracking-widest opacity-80">Risque</p>
              <p className="font-serif-display text-3xl font-medium leading-none" data-testid="scam-risk-score">{score}<span className="text-sm opacity-70">/100</span></p>
            </div>
          )}
        </div>
        {action && (
          <div className="px-5 py-4 bg-white border-t border-slate-200">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">🎯 À faire MAINTENANT</p>
            <p className={`text-base font-medium ${meta.text}`} data-testid="scam-action-now">{action}</p>
          </div>
        )}
      </div>

      {/* Toggle for details */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between border border-slate-200 hover:border-slate-900 rounded-sm px-4 py-3 bg-white transition-colors"
        data-testid="scam-toggle-details"
      >
        <span className="text-sm font-medium text-slate-900">
          {open ? "Masquer les détails" : "Voir l'analyse détaillée"}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>

      {open && (
        <div className="space-y-6" data-testid="scam-details">
          <BulletList
            title={isVoice ? "🚩 Indices d'arnaque dans la transcription" : "🚩 Signaux d'alerte (red flags)"}
            items={flags}
            testid="scam-flags"
            highlight
          />
          <BulletList
            title={isVoice ? "❓ Questions à poser AU TÉLÉPHONE pour vérifier" : "✅ Vérifications à faire"}
            items={verifications}
            testid="scam-verifications"
          />
          <BulletList title="🚫 À NE PAS faire" items={dontList} testid="scam-dont" />

          {signalement && (
            <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="scam-report">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">📞 Signaler officiellement</p>
              <p className="text-sm text-slate-800">{signalement}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <a href="mailto:suspect@safeonweb.be" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-3 py-2 text-xs" data-testid="scam-report-safeonweb">
                  <Send className="h-3.5 w-3.5" /> suspect@safeonweb.be
                </a>
                <a href="tel:080012033" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-sm px-3 py-2 text-xs" data-testid="scam-report-phone">
                  <Phone className="h-3.5 w-3.5" /> 0800/120.33
                </a>
              </div>
            </div>
          )}

          {/* Original content reminder */}
          {!isVoice && (alert.body || alert.subject || alert.sender) && (
            <div className="border border-slate-200 rounded-sm p-4 bg-white" data-testid="scam-original">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">📨 Contenu analysé</p>
              <div className="space-y-1 text-xs text-slate-600">
                {alert.channel && <p><strong>Canal :</strong> {alert.channel}</p>}
                {alert.sender && <p><strong>Expéditeur :</strong> {alert.sender}</p>}
                {alert.subject && <p><strong>Objet :</strong> {alert.subject}</p>}
                {alert.files && alert.files.length > 0 && (
                  <p><strong>Fichiers joints :</strong> {alert.files.length}</p>
                )}
              </div>
              {alert.body && (
                <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-48 overflow-auto">
                  {alert.body}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

